import React, { useEffect, useState, useRef, Fragment, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { ConsultationTypeBadge } from '../../components/ui/ConsultationTypeBadge';
import { ClinicianServiceCards, ClinicianTab } from './ClinicianServiceCards';
import { AmendFile } from './AmendFile';
import { Encounter, Patient, ClinicalNote, LabRequest, RadiologyRequest, Prescription } from '../../types';
import { Stethoscope, FileText, FlaskConical, Scan, Pill, CheckCircle, History, X, ZoomIn, ZoomOut, Eye, Printer, Download, Trash2, RotateCcw } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { SickNoteForm } from './SickNoteForm';
/**
 * Capture a DOM element and export it as a properly paginated A4 PDF.
 * A4 layout: 210mm × 297mm, margins 20mm top/bottom, 18mm left/right.
 */
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isAMHTest, hcgStructure, isHCGTest, isHCGAbnormal, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest } from '../../store/labStructures';
type CViewingTest = {
  kind: 'lab';
  labId: string;
  testName?: string;
} | {
  kind: 'rad';
  radId: string;
} | null;
export const ClinicianDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters,
    logAudit
  } = useData();
  const [activeTab, setActiveTab] = useState<ClinicianTab>('queue');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(null);
  // Live encounter - always reads from context so lab/rad updates appear instantly
  const selectedEncounter = selectedEncounterId ? encounters.find(e => e.id === selectedEncounterId) || null : null;
  // Image zoom state
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  // View Test File modal state
  const [viewingTest, setViewingTest] = useState<CViewingTest>(null);
  const [testImageZoom, setTestImageZoom] = useState<number>(100);
  const [isGeneratingTestPdf, setIsGeneratingTestPdf] = useState(false);
  const testModalRef = useRef<HTMLDivElement>(null);
  // Remove Test Modal state
  const [removeTestModal, setRemoveTestModal] = useState<{
    isOpen: boolean;
    labId: string;
    testName: string;
    reason: string;
  }>({
    isOpen: false,
    labId: '',
    testName: '',
    reason: ''
  });
  const closeTestModal = () => {
    setViewingTest(null);
    setTestImageZoom(100);
  };
  const handlePrintTestModal = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadTestPdf = async () => {
    if (!testModalRef.current) return;
    setIsGeneratingTestPdf(true);
    try {
      const filename = `BMED_TestResult_${Date.now()}.pdf`;
      await captureToA4Pdf(testModalRef.current, filename);
    } catch (err) {
      console.error('Test PDF failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingTestPdf(false);
    }
  };
  // Modals state
  const [showLabModal, setShowLabModal] = useState(false);
  const [showRadModal, setShowRadModal] = useState(false);
  const [showRxModal, setShowRxModal] = useState(false);
  const [showSickNoteModal, setShowSickNoteModal] = useState(false);
  // Clinical Notes State
  const [notes, setNotes] = useState<Partial<ClinicalNote>>({
    presentingComplaint: '',
    historyOfPresentingComplaint: '',
    pastMedicalHistory: [],
    drugHistory: '',
    familyMedicalHistory: [],
    obgynHistory: '',
    socialHistory: {
      alcohol: '',
      smoking: '',
      occupation: '',
      mentalStatus: ''
    },
    reviewOfSystems: {
      rs: '',
      cvs: '',
      cns: '',
      git: '',
      gut: '',
      mss: '',
      is: ''
    },
    onExamination: '',
    onExaminationDetails: {
      gc: '',
      febrile: '',
      pjc: '',
      s1s2: '',
      rs: '',
      pa: ''
    },
    significantFindings: [],
    workingDiagnosis: []
  });
  const [reviewDateNotes, setReviewDateNotes] = useState('');
  // Lab Request State
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [otherTests, setOtherTests] = useState('');
  // Rad Request State
  const [scanType, setScanType] = useState('');
  // Rx State
  const [rxForm, setRxForm] = useState({
    drugName: '',
    dose: '',
    route: '',
    frequency: '',
    days: ''
  });
  // Active queue only. Completed visits live in Patient Files and can be
  // reopened from there via the "Amend File" flow.
  const pendingEncounters = encounters.filter(e => e.stage !== 'COMPLETED');
  const completedEncounters = encounters.filter(e => e.stage === 'COMPLETED');
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const filteredCompleted = completedEncounters.filter(enc => {
    const p = getPatient(enc.patientId);
    if (!p) return false;
    const q = searchQuery.toLowerCase();
    return p.firstName.toLowerCase().includes(q) || p.lastName.toLowerCase().includes(q) || p.bmcFileNumber.toLowerCase().includes(q);
  }).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  const handleSelectEncounter = (encounter: Encounter) => {
    setSelectedEncounterId(encounter.id);
    setReviewDateNotes(encounter.reviewDateNotes || '');
    if (encounter.clinicalNote) {
      setNotes(encounter.clinicalNote);
    } else {
      setNotes({
        presentingComplaint: '',
        historyOfPresentingComplaint: '',
        pastMedicalHistory: [],
        drugHistory: '',
        familyMedicalHistory: [],
        obgynHistory: '',
        socialHistory: {
          alcohol: '',
          smoking: '',
          occupation: '',
          mentalStatus: ''
        },
        reviewOfSystems: {
          rs: '',
          cvs: '',
          cns: '',
          git: '',
          gut: '',
          mss: '',
          is: ''
        },
        onExamination: '',
        onExaminationDetails: {
          gc: '',
          febrile: '',
          pjc: '',
          s1s2: '',
          rs: '',
          pa: ''
        },
        significantFindings: [],
        workingDiagnosis: []
      });
    }
    // Move encounter back to CLINICIAN stage if returning from investigations
    if (encounter.stage === 'INVESTIGATIONS') {
      updateEncounter(encounter.id, {
        stage: 'CLINICIAN',
        lastAttendingClinician: currentUser?.fullName
      });
    } else if (currentUser && encounter.lastAttendingClinician !== currentUser.fullName) {
      // Record that this clinician opened/consulted the file.
      updateEncounter(encounter.id, {
        lastAttendingClinician: currentUser.fullName
      });
    }
  };
  const handleSaveNotes = () => {
    if (!selectedEncounter || !currentUser) return;
    const isAmending = selectedEncounter.stage === 'COMPLETED';
    let editHistory = selectedEncounter.clinicalNote?.editHistory || [];
    if (isAmending) {
      const reason = prompt('Please enter a mandatory reason for modifying this completed consultation:');
      if (!reason || !reason.trim()) {
        alert('A reason is required to amend a completed consultation.');
        return;
      }
      const previousNote = selectedEncounter.clinicalNote!;
      const {
        editHistory: _oldHistory,
        ...noteSnapshot
      } = previousNote;
      editHistory = [...editHistory, {
        editedAt: new Date().toISOString(),
        editedBy: currentUser.id,
        editedByName: currentUser.fullName,
        reason: reason.trim(),
        previousNote: noteSnapshot as any
      }];
      logAudit({
        type: 'consultation.edit',
        encounterId: selectedEncounter.id,
        patientId: selectedEncounter.patientId,
        patientName: `${getPatient(selectedEncounter.patientId)?.firstName} ${getPatient(selectedEncounter.patientId)?.lastName}`,
        userId: currentUser.id,
        userName: currentUser.fullName,
        userRole: currentUser.role,
        reason: reason.trim(),
        previousContent: JSON.stringify(noteSnapshot),
        newContent: JSON.stringify(notes)
      });
    }
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: isAmending ? selectedEncounter.clinicalNote!.recordedAt : new Date().toISOString(),
      recordedBy: isAmending ? selectedEncounter.clinicalNote!.recordedBy : currentUser.id,
      clinicianName: isAmending ? selectedEncounter.clinicalNote!.clinicianName : currentUser.fullName,
      editHistory
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName
    });
    alert(isAmending ? 'Amendment saved and logged successfully.' : 'Clinical notes saved successfully.');
  };
  const handleRequestLab = () => {
    if (!selectedEncounter || !currentUser || selectedTests.length === 0 && !otherTests) return;
    const newLab: LabRequest = {
      id: `lab-${Date.now()}`,
      tests: selectedTests,
      otherTests,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      labRequests: [...selectedEncounter.labRequests, newLab],
      stage: 'INVESTIGATIONS',
      pendingLab: true
    });
    setShowLabModal(false);
    setSelectedTests([]);
    setOtherTests('');
    // File stays open — clinician closes/saves/completes when ready.
    alert('Laboratory tests requested. The patient file remains open — you can continue your consultation.');
  };
  const handleRequestRad = () => {
    if (!selectedEncounter || !currentUser || !scanType) return;
    const newRad: RadiologyRequest = {
      id: `rad-${Date.now()}`,
      scanType,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      radiologyRequests: [...selectedEncounter.radiologyRequests, newRad],
      stage: 'INVESTIGATIONS',
      pendingRadiology: true
    });
    setShowRadModal(false);
    setScanType('');
    // File stays open — clinician closes/saves/completes when ready.
    alert('Radiology scan requested. The patient file remains open — you can continue your consultation.');
  };
  const handleAddRx = () => {
    if (!selectedEncounter || !currentUser || !rxForm.drugName) return;
    if (selectedEncounter.prescriptions.length >= 20) {
      alert('You have reached the maximum of 20 drugs per prescription. Remove an existing item before adding another.');
      return;
    }
    const newRx: Prescription = {
      id: `rx-${Date.now()}`,
      ...rxForm,
      status: 'Pending',
      prescribedAt: new Date().toISOString(),
      prescribedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      prescriptions: [...selectedEncounter.prescriptions, newRx]
    });
    setRxForm({
      drugName: '',
      dose: '',
      route: '',
      frequency: '',
      days: ''
    });
  };
  // Delete a single prescription. Only allowed while it is still Pending —
  // once Pharmacy has dispensed (or marked unavailable), it stays on record.
  const handleDeletePrescription = (rxId: string) => {
    if (!selectedEncounter) return;
    const rx = selectedEncounter.prescriptions.find(p => p.id === rxId);
    if (!rx) return;
    if (rx.status !== 'Pending') {
      alert('This drug has already been processed by Pharmacy and cannot be removed from the record.');
      return;
    }
    if (!confirm(`Remove "${rx.drugName}" from the prescription list? This cannot be undone.`)) return;
    updateEncounter(selectedEncounter.id, {
      prescriptions: selectedEncounter.prescriptions.filter(p => p.id !== rxId)
    });
  };
  // Recall (remove) a single ordered lab test that has not yet been completed.
  // - For tests inside lab.tests[]: drop just that test from the array.
  // - If that empties the request (and no otherTests), remove the whole request.
  // - For an "other test" entry: clear lab.otherTests on that request, or
  //   remove the request entirely if it has no tests left either.
  const handleRecallLabTest = (labId: string, testName: string, isOther = false) => {
    if (!selectedEncounter) return;
    const lab = selectedEncounter.labRequests.find(l => l.id === labId);
    if (!lab) return;
    if (lab.status === 'Completed') {
      alert('This test has already been completed by the laboratory and cannot be recalled.');
      return;
    }
    if (!confirm(`Recall the lab test "${testName}"? The laboratory will no longer see this request.`)) return;
    let updatedLabs = selectedEncounter.labRequests.map(l => {
      if (l.id !== labId) return l;
      if (isOther) return {
        ...l,
        otherTests: ''
      };
      return {
        ...l,
        tests: l.tests.filter(t => t !== testName)
      };
    });
    // Drop any request that now has no tests AND no otherTests.
    updatedLabs = updatedLabs.filter(l => l.tests.length > 0 || l.otherTests.trim().length > 0);
    const stillPendingLab = updatedLabs.some(l => l.status === 'Pending');
    updateEncounter(selectedEncounter.id, {
      labRequests: updatedLabs,
      pendingLab: stillPendingLab
    });
  };
  // Recall (remove) a pending radiology request.
  const handleRecallRadiology = (radId: string) => {
    if (!selectedEncounter) return;
    const rad = selectedEncounter.radiologyRequests.find(r => r.id === radId);
    if (!rad) return;
    if (rad.status === 'Completed') {
      alert('This scan has already been completed by radiology and cannot be recalled.');
      return;
    }
    if (!confirm(`Recall the radiology request "${rad.scanType}"? Radiology will no longer see this request.`)) return;
    const updatedRads = selectedEncounter.radiologyRequests.filter(r => r.id !== radId);
    const stillPendingRad = updatedRads.some(r => r.status === 'Pending');
    updateEncounter(selectedEncounter.id, {
      radiologyRequests: updatedRads,
      pendingRadiology: stillPendingRad
    });
  };
  const handleRemoveTestConfirm = () => {
    if (!selectedEncounter || !currentUser || !removeTestModal.reason.trim()) return;
    const lab = selectedEncounter.labRequests.find(l => l.id === removeTestModal.labId);
    if (!lab) return;
    const now = new Date().toISOString();
    const newEntry = {
      testName: removeTestModal.testName,
      removedAt: now,
      removedBy: currentUser.id,
      reason: removeTestModal.reason
    };
    const updatedLabRequests = selectedEncounter.labRequests.map(l => {
      if (l.id !== removeTestModal.labId) return l;
      return {
        ...l,
        removedTestNames: [...(l.removedTestNames || []), removeTestModal.testName],
        removedTestEntries: [...(l.removedTestEntries || []), newEntry]
      };
    });
    updateEncounter(selectedEncounter.id, {
      labRequests: updatedLabRequests
    });
    const patient = patients.find(p => p.id === selectedEncounter.patientId);
    logAudit({
      type: 'lab.test.remove',
      encounterId: selectedEncounter.id,
      patientId: selectedEncounter.patientId,
      patientName: patient ? `${patient.firstName} ${patient.lastName}` : 'Unknown',
      userId: currentUser.id,
      userName: currentUser.fullName,
      userRole: currentUser.role,
      reason: removeTestModal.reason,
      labRequestId: removeTestModal.labId,
      testName: removeTestModal.testName
    });
    setRemoveTestModal({
      isOpen: false,
      labId: '',
      testName: '',
      reason: ''
    });
  };
  const handleCompleteVisit = () => {
    if (!selectedEncounter || !currentUser) return;
    // Save notes one last time
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    const hasPrescriptions = selectedEncounter.prescriptions.length > 0;
    const hasPendingDispense = selectedEncounter.prescriptions.some(rx => rx.status === 'Pending');
    // If prescriptions exist and none have been dispensed yet, send to pharmacy.
    // If pharmacy has already processed them (returned to clinician), complete the visit.
    const sendToPharmacy = hasPrescriptions && hasPendingDispense;
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName,
      stage: sendToPharmacy ? 'PHARMACY' : 'COMPLETED',
      completedAt: sendToPharmacy ? undefined : new Date().toISOString()
    });
    alert(sendToPharmacy ? 'Visit saved. Patient forwarded to Pharmacy.' : 'Visit completed successfully.');
    setSelectedEncounterId(null);
  };
  const handleSendToPharmacy = () => {
    if (!selectedEncounter || !currentUser) return;
    const pendingRx = selectedEncounter.prescriptions.filter(rx => rx.status === 'Pending');
    if (pendingRx.length === 0) {
      alert('No pending prescriptions to send to pharmacy.');
      return;
    }
    // Save current notes
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName,
      stage: 'PHARMACY'
    });
    // File stays open — clinician decides when to close, save draft, or complete the visit.
    alert(`Prescription sent to Pharmacy (${pendingRx.length} drug${pendingRx.length > 1 ? 's' : ''}). The patient file remains open — you can continue your consultation.`);
  };
  if (selectedEncounter) {
    const patient = getPatient(selectedEncounter.patientId);
    if (!patient) return <div data-id="element-5879" data-mp="2b85naphspj4k">Patient not found</div>;
    const isCompleted = selectedEncounter.stage === 'COMPLETED';
    const isOriginalClinician = selectedEncounter.clinicalNote?.recordedBy === currentUser?.id;
    const isAdmin = currentUser?.role === 'Administrator';
    const canEdit = !isCompleted || isOriginalClinician || isAdmin;
    return <div className="space-y-6 pb-20" data-id="element-5880" data-mp="4p1d9ra2rmvz">
        {isCompleted && !canEdit && <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded flex items-center mb-4" data-id="element-5881" data-mp="4mxaxdrtqvxt">
            <span className="text-sm font-medium" data-id="element-5882" data-mp="3rtr09yqte3bj">
              This consultation is completed and can only be amended by the
              original clinician (
              {selectedEncounter.clinicalNote?.clinicianName}) or an
              Administrator.
            </span>
          </div>}
        {isCompleted && canEdit && <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded flex items-center mb-4" data-id="element-5883" data-mp="3d3s4u3qn95zr">
            <History className="w-5 h-5 mr-2 text-amber-600" data-id="element-5884" data-mp="393rdjiig50ba" />
            <span className="text-sm font-medium" data-id="element-5885" data-mp="344oimqw6kah1">
              You are viewing a completed consultation. Any edits saved will be
              logged in the audit trail.
            </span>
          </div>}
        {/* Sticky Action Bar */}
        <div className="flex justify-between items-center sticky top-0 bg-gray-100 z-10 py-4 border-b border-gray-200" data-id="element-5886" data-mp="2u6qus4i8gfoy">
          <div className="min-w-0" data-id="element-5887" data-mp="2phg4bh7krp0u">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5888" data-mp="2phptj2ksps5h">
              {isCompleted ? 'Completed Consultation' : 'Consultation'}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-700" data-id="element-5889" data-mp="2bk1am0aagv32">
              <span data-id="element-5890" data-mp="2fl3b0kfxzd6m">
                <span className="text-gray-500" data-id="element-5891" data-mp="3394hlntk8jje">Patient:</span>{' '}
                <span className="font-semibold text-gray-900" data-id="element-5892" data-mp="33axlwwgparo8">
                  {patient.firstName} {patient.lastName}
                </span>
              </span>
              <span className="flex items-center gap-2" data-id="element-5893" data-mp="2ufrejt1s81jl">
                <span className="text-gray-500" data-id="element-5894" data-mp="2yi4g059hxdak">Consultation Type:</span>
                {selectedEncounter.consultationType ? <ConsultationTypeBadge type={selectedEncounter.consultationType} data-id="element-5895" data-mp="3m6ns6vvo2a7x" /> : <span className="italic text-gray-500" data-id="element-5896" data-mp="3r0jmnocq1eyc">Not specified</span>}
              </span>
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-5897" data-mp="1al52j59veqbv">
            <Button variant="outline" onClick={() => setSelectedEncounterId(null)} data-id="element-5898" data-mp="1ak3iibavmti0">
              Close File
            </Button>
            <Button variant="outline" className="text-amber-700 border-amber-600 hover:bg-amber-50" onClick={() => {
            const reason = prompt('Send this file back to Nursing so the vitals can be corrected. Please provide a brief reason (optional):', '') ?? '';
            if (confirm('Are you sure you want to send this file back to Nursing? The patient will leave your queue until the nurse re-submits the corrected vitals.')) {
              const notePrefix = reason ? `[Returned to Nursing by ${currentUser?.fullName || 'Clinician'}: ${reason}]` : `[Returned to Nursing by ${currentUser?.fullName || 'Clinician'}]`;
              updateEncounter(selectedEncounter.id, {
                stage: 'NURSING',
                vitalsReturnNote: notePrefix,
                vitalsReturnedAt: new Date().toISOString()
              } as any);
              alert('File sent back to Nursing for vitals correction. It will return to your queue once the nurse re-submits the vitals.');
              setSelectedEncounterId(null);
            }
          }} data-id="element-5899" data-mp="1kbhlit21ebfw">
              ↩ Send to Nursing
            </Button>
            <Button variant="outline" onClick={() => setShowSickNoteModal(true)} data-id="element-5900" data-mp="1o73vnh3qga8r">
              <FileText className="w-4 h-4 mr-2" data-id="element-5901" data-mp="1t5njjn1vzw3r" /> Generate Sick Note
            </Button>
            <Button variant="primary" onClick={handleSaveNotes} data-id="element-5902" data-mp="239u8mqmhz20t">
              {selectedEncounter.stage === 'COMPLETED' ? 'Save Amendment' : 'Save Draft'}
            </Button>
            {selectedEncounter.stage !== 'COMPLETED' && <Button variant="success" className="bg-green-600 text-white hover:bg-green-700" onClick={handleCompleteVisit} data-id="element-5903" data-mp="1te0mqgmw1w6d">
                <CheckCircle className="w-4 h-4 mr-2" data-id="element-5904" data-mp="2lztoqo0d16s3" /> Complete Visit
              </Button>}
          </div>
        </div>

        {showSickNoteModal && <SickNoteForm encounter={selectedEncounter} patient={patient} clinician={currentUser} onClose={() => setShowSickNoteModal(false)} data-id="element-5905" data-mp="2c9qpw76k4ylu" />}

        {/* Medical Form */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6" data-id="element-5906" data-mp="34t7fegx1ir1w">
          <fieldset disabled={!canEdit} className="group" data-id="element-5907" data-mp="2zytgw5n303t0">
            {/* ═══ FORM HEADER ═══ */}
            {selectedEncounter.lastAttendingClinician && selectedEncounter.lastAttendingClinician !== currentUser?.fullName && <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded flex items-center" data-id="element-5908" data-mp="odkg1zalgl12">
                  <Stethoscope className="w-5 h-5 mr-2 text-blue-600" data-id="element-5909" data-mp="tat2wd6otd8n" />
                  <span className="text-sm font-medium" data-id="element-5910" data-mp="fbtdv4f9iwby">
                    This file was last attended to by:{' '}
                    <span className="font-bold" data-id="element-5911" data-mp="kch1rh872x1y">
                      {selectedEncounter.lastAttendingClinician}
                    </span>
                  </span>
                </div>}
            <div className="flex justify-between items-start mb-6" data-id="element-5912" data-mp="aeb112y4jwyo">
              <div className="flex items-center" data-id="element-5913" data-mp="1zsfx1rwpqyks">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-5914" data-mp="1zspn1fhrd5qh" />
                <div data-id="element-5915" data-mp="3kaibwh05irmw">
                  <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-5916" data-mp="3kb07rciwj3ua">
                    BMED MEDICAL CENTRE
                  </h1>
                  <p className="text-sm italic text-gray-600" data-id="element-5917" data-mp="2vjzz4pk22xw">
                    Caring Healing Leading
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-5918" data-mp="bsu8iiazndz4">
                <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-5919" data-mp="7sttavg2eyr0">
                  Confidential
                </p>
                <p className="text-sm" data-id="element-5920" data-mp="lrtl2byvjtre">
                  <span className="font-semibold" data-id="element-5921" data-mp="gtrr2vzwua15">Date:</span>{' '}
                  {new Date(selectedEncounter.startedAt).toLocaleDateString()}
                </p>
                <p className="text-sm" data-id="element-5922" data-mp="2nk7x4ueqm3zw">
                  <span className="font-semibold" data-id="element-5923" data-mp="2ilel76f3ml1u">File No:</span>{' '}
                  {patient.bmcFileNumber}
                </p>
              </div>
            </div>

            {/* ═══ PERSONAL DETAILS ═══ */}
            <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-5924" data-mp="21q0mv9aijkyo">
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-5925" data-mp="26hsihy8j0gvo">
                <div className="col-span-1 font-semibold" data-id="element-5926" data-mp="2bglv7ofzo3us">1.</div>
                <div className="col-span-6" data-id="element-5927" data-mp="2bftzmdme664i">
                  <span className="font-semibold" data-id="element-5928" data-mp="1dt3d3n0ftpe4">Full Names:</span>{' '}
                  {patient.firstName} {patient.lastName}
                </div>
                <div className="col-span-3" data-id="element-5929" data-mp="1isg7cstn1yqq">
                  <span className="font-semibold" data-id="element-5930" data-mp="1nraqjvvippbd">Sex:</span> {patient.sex}
                </div>
                <div className="col-span-2" data-id="element-5931" data-mp="1spug069blj8h">
                  <span className="font-semibold" data-id="element-5932" data-mp="1srp1bloi7dd3">Age:</span> {patient.age}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-5933" data-mp="2z5wrasme9a2l">
                <div className="col-span-1 font-semibold" data-id="element-5934" data-mp="343pv5f085rzp">2.</div>
                <div className="col-span-11" data-id="element-5935" data-mp="si769qm96c4p">
                  <span className="font-semibold" data-id="element-5936" data-mp="sh4jnuvyqvul">Marital Status:</span>{' '}
                  {patient.maritalStatus}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-5937" data-mp="ircmcswhbomf">
                <div className="col-span-1 font-semibold" data-id="element-5938" data-mp="dtjfdxncqql3">3.</div>
                <div className="col-span-7" data-id="element-5939" data-mp="dr6xfkhlgg81">
                  <span className="font-semibold" data-id="element-5940" data-mp="8uzqbodgoyoi">Home Address:</span>{' '}
                  {patient.address}
                </div>
                <div className="col-span-4" data-id="element-5941" data-mp="4r1sdgzvhtx1">
                  <span className="font-semibold" data-id="element-5942" data-mp="3vy4hm2w5hq4m">Contact:</span>{' '}
                  {patient.contact}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-5943" data-mp="3r14eo0ull3hq">
                <div className="col-span-1 font-semibold" data-id="element-5944" data-mp="1u5yitdgbon7u">4.</div>
                <div className="col-span-11" data-id="element-5945" data-mp="1p7oloxb1fcjl">
                  <span className="font-semibold" data-id="element-5946" data-mp="1p8znirkxaa82">Occupation:</span>{' '}
                  {patient.occupation || '_________________________'}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-5947" data-mp="2ai7tl835c93e">
                <div className="col-span-1 font-semibold" data-id="element-5948" data-mp="2p6viwwmefmkw">5.</div>
                <div className="col-span-5" data-id="element-5949" data-mp="2k92fgbchd6ob">
                  <span className="font-semibold" data-id="element-5950" data-mp="2ka540cmbyx12">Next of Kin:</span>{' '}
                  {patient.nextOfKin}
                </div>
                <div className="col-span-3" data-id="element-5951" data-mp="1wl2qngyqbqcd">
                  <span className="font-semibold" data-id="element-5952" data-mp="1rky4pbf1y3er">Contact:</span>{' '}
                  {patient.nokContact}
                </div>
                <div className="col-span-3" data-id="element-5953" data-mp="25lha0as4uvd8">
                  <span className="font-semibold" data-id="element-5954" data-mp="21ikoocb7kl5l">Relationship:</span>{' '}
                  {patient.nokRelationship}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-5955" data-mp="3gx9ugm6xwmte">
                <div className="col-span-1 font-semibold" data-id="element-5956" data-mp="3cwy7v3v0ocfa">6.</div>
                <div className="col-span-11" data-id="element-5957" data-mp="3kgr1wlkk5dm2">
                  <span className="font-semibold" data-id="element-5958" data-mp="3kfpgpo9tvaqm">Allergies:</span>{' '}
                  <span className="text-red-600 font-medium" data-id="element-5959" data-mp="3pgv91kzksvzm">
                    {patient.allergies}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2" data-id="element-5960" data-mp="3fhe455s5em4p">
                <div className="col-span-1 font-semibold" data-id="element-5961" data-mp="3fi5xsb26q9sc">7.</div>
                <div className="col-span-11" data-id="element-5962" data-mp="735k4m3oz3o5">
                  <span className="font-semibold" data-id="element-5963" data-mp="74glygdku1cm">HIV Status:</span>{' '}
                  <span className="ml-4" data-id="element-5964" data-mp="c0nvsk93fwzu">
                    <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-5965" data-mp="3thync31z6jyq">
                      Positive {patient.hivStatus === 'Positive' ? '✓' : ''}
                    </span>
                    <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-5966" data-mp="urf19apyn38y">
                      Negative {patient.hivStatus === 'Negative' ? '✓' : ''}
                    </span>
                    <span className={`inline-block px-2 py-0.5 border border-black ${patient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-5967" data-mp="vmhapugv5450">
                      Unknown {patient.hivStatus === 'Unknown' ? '✓' : ''}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* ═══ NURSING OBSERVATIONS ═══ */}
            <div className="mb-6" data-id="element-5968" data-mp="bzt4wmrhdasa">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-5969" data-mp="bwndv1zajytx">
                Nursing Observations
              </h3>
              <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-5970" data-mp="2xs79osc4mq3">
                <thead data-id="element-5971" data-mp="3u54qe9cs3a8n">
                  <tr className="bg-gray-50" data-id="element-5972" data-mp="3u6piztfj8j3v">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-5973" data-mp="2eloohe9lr0oy">
                        {n}
                      </th>)}
                  </tr>
                  <tr className="bg-gray-100" data-id="element-5974" data-mp="2nltlqvl30ipe">
                    <th className="border border-black p-1" data-id="element-5975" data-mp="1qwcien9kn8qv">Temp</th>
                    <th className="border border-black p-1" data-id="element-5976" data-mp="1qtzs9mtnts2q">Pulse</th>
                    <th className="border border-black p-1" data-id="element-5977" data-mp="1vvyw0xtbar07">Resp</th>
                    <th className="border border-black p-1" data-id="element-5978" data-mp="1zw8za2ppz3bd">BP</th>
                    <th className="border border-black p-1" data-id="element-5979" data-mp="1zuxubzkjfpio">SpO2</th>
                    <th className="border border-black p-1" data-id="element-5980" data-mp="24p22gm5ji3lc">Weight (Kg)</th>
                    <th className="border border-black p-1" data-id="element-5981" data-mp="184jp0rqlblim">Height</th>
                    <th className="border border-black p-1" data-id="element-5982" data-mp="1c6y8ne232pay">BMI</th>
                    <th className="border border-black p-1" data-id="element-5983" data-mp="1c53kzrzfgj36">Pain Score</th>
                    <th className="border border-black p-1" data-id="element-5984" data-mp="t3crx7pkl2rt">AVPU</th>
                  </tr>
                </thead>
                <tbody data-id="element-5985" data-mp="o5bassbpfdiz">
                  <tr data-id="element-5986" data-mp="12se9317upq7d">
                    <td className="border border-black p-2 font-medium" data-id="element-5987" data-mp="12swfgqo89kt0">
                      {selectedEncounter.vitals?.temp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-5988" data-mp="xzl6aivy0rxx">
                      {selectedEncounter.vitals?.pulse || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-5989" data-mp="16sy7s982p1vm">
                      {selectedEncounter.vitals?.resp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-5990" data-mp="1gs5s5gd85ws8">
                      {selectedEncounter.vitals?.bp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-5991" data-mp="3nj59icn75gw1">
                      {selectedEncounter.vitals?.sp02 || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-5992" data-mp="1y0yczu746n24">
                      {selectedEncounter.vitals?.weight || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-5993" data-mp="232nmh5eouso3">
                      {selectedEncounter.vitals?.height || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-5994" data-mp="1t9q4r1hit7qj">
                      {selectedEncounter.vitals?.bmi || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-5995" data-mp="1jclpllm3ecj8">
                      {selectedEncounter.vitals?.painScore || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-5996" data-mp="15end6lrc0q6d">
                      {selectedEncounter.vitals?.avpu || ''}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ═══ STAT DOSES ADMINISTERED BY NURSING ═══ */}
            {selectedEncounter.statDoses && selectedEncounter.statDoses.length > 0 && <div className="border-2 border-black mb-6" data-id="element-5997" data-mp="336ohqn4smpaa">
                  <h3 className="font-bold uppercase bg-yellow-100 p-2 border-b border-black text-center text-sm" data-id="element-5998" data-mp="2y8n1e9yr542i">
                    ⚡ Stat Doses Administered by Nursing
                  </h3>
                  <table className="w-full border-collapse text-sm" data-id="element-5999" data-mp="2ocjw4v8lspbc">
                    <thead data-id="element-6000" data-mp="3lyzdzy4xzozf">
                      <tr className="bg-gray-100" data-id="element-6001" data-mp="3ly7jkqn2zxaq">
                        <th className="border border-black p-1.5 w-10" data-id="element-6002" data-mp="3m13julmeiy2c">No.</th>
                        <th className="border border-black p-1.5 text-left" data-id="element-6003" data-mp="3h0xtr9vs5jmd">
                          Drug Name
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-6004" data-mp="37aw8eputafb5">
                          Dose
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-6005" data-mp="8l4d8v1g885b">
                          Route
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-6006" data-mp="3rt951o0u5h3">
                          Time Given
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-6007" data-mp="g4wx5jx9zsyd">
                          Nurse
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-6008" data-mp="q2bb9cvq85ni">
                      {selectedEncounter.statDoses.map((dose, idx) => <tr key={dose.id} data-id="element-6009" data-mp="v1xmxhvwpdu7" data-mpi={idx}>
                          <td className="border border-black p-1.5 text-center" data-id="element-6010" data-mp="uz1sw4n6j9b1">
                            {idx + 1}
                          </td>
                          <td className="border border-black p-1.5 font-medium" data-id="element-6011" data-mp="13t6eo9nghujp">
                            {dose.drugName}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-6012" data-mp="18udlo6hryppb">
                            {dose.dose}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-6013" data-mp="1ipxg0ogoxgby">
                            {dose.route}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-6014" data-mp="23zuc3iww3vu4">
                            {dose.givenAt ? new Date(dose.givenAt).toLocaleString() : ''}
                          </td>
                          <td className="border border-black p-1.5 italic text-xs" data-id="element-6015" data-mp="282r0jq942m5z">
                            {dose.nurseName || ''}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}

            {/* ═══ CLINICAL NOTES ═══ */}
            <div className="space-y-5 mb-6 text-sm" data-id="element-6016" data-mp="1u4snco6j0vs2">
              {/* PRESENTING COMPLAINT */}
              <div className="border-2 border-black" data-id="element-6017" data-mp="1pbg9n93dy9e3">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-6018" data-mp="11morlajif7f1">
                  Presenting Complaint
                </h3>
                <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.presentingComplaint} onChange={e => setNotes({
                ...notes,
                presentingComplaint: e.target.value
              })} data-id="element-6019" data-mp="wm0zeljjokj8" />
              </div>

              {/* HISTORY OF PRESENTING COMPLAINT */}
              <div className="border-2 border-black" data-id="element-6020" data-mp="3s0r9h6qx3edr">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-6021" data-mp="3w0roashxhrlc">
                  History of Presenting Complaint
                </h3>
                <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.historyOfPresentingComplaint} onChange={e => setNotes({
                ...notes,
                historyOfPresentingComplaint: e.target.value
              })} data-id="element-6022" data-mp="3i10ivg5h9jiy" />
              </div>

              {/* PAST MEDICAL HISTORY */}
              <div className="border-2 border-black" data-id="element-6023" data-mp="1yq94j3d337g5">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-6024" data-mp="1k01rx49rcind">
                  Past Medical History / General Co-Morbidities
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-6025" data-mp="2cofs9qobjr23">
                  <tbody data-id="element-6026" data-mp="2cp8q225av683">
                    {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => {
                    const checked = notes.pastMedicalHistory?.includes(condition) || false;
                    return <tr key={condition} className="border-b border-gray-300" data-id="element-6027" data-mp="oqogh83pos9f">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-6028" data-mp="jr23mzrspe15">
                            {condition}
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-6029" data-mp="tp87vo12qw96">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-6030" data-mp="5zxb0hmuhm0b">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={checked} onChange={e => {
                            const current = notes.pastMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                pastMedicalHistory: [...current, condition]
                              });
                            } else {
                              setNotes({
                                ...notes,
                                pastMedicalHistory: current.filter(c => c !== condition)
                              });
                            }
                          }} data-id="element-6031" data-mp="11ccqnr5lcei" />
                              {checked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-6032" data-mp="19fzfxbf9oy6s">
                            <input type="text" placeholder="Notes (optional)" disabled={!checked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.pastMedicalHistoryNotes?.[condition] || ''} onChange={e => setNotes({
                          ...notes,
                          pastMedicalHistoryNotes: {
                            ...(notes.pastMedicalHistoryNotes || {}),
                            [condition]: e.target.value
                          }
                        })} data-id="element-6033" data-mp="19f7jjyduiwfg" />
                          </td>
                        </tr>;
                  })}
                  </tbody>
                </table>
              </div>

              {/* DRUG HISTORY */}
              <div className="border-2 border-black" data-id="element-6034" data-mp="7iynmdflhqnd">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-6035" data-mp="ci1v04j1s8yi">
                  Drug History
                </h3>
                <textarea className="w-full p-3 min-h-[60px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.drugHistory} onChange={e => setNotes({
                ...notes,
                drugHistory: e.target.value
              })} data-id="element-6036" data-mp="3tp3wmsafb2w3" />
                <div className="p-3 border-t border-gray-300 bg-gray-50" data-id="element-6037" data-mp="3b0gtifbt4nl5">
                  <span className="font-semibold" data-id="element-6038" data-mp="3fxfnmxvh0ujc">Any known allergy:</span>{' '}
                  <span className="text-red-600 font-medium" data-id="element-6039" data-mp="31ypq5h4qosug">
                    {patient.allergies}
                  </span>
                </div>
              </div>

              {/* FAMILY MEDICAL HISTORY */}
              <div className="border-2 border-black" data-id="element-6040" data-mp="2nb8xeb4d4u0y">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-6041" data-mp="2ndv2m43s6xg0">
                  Family Medical History
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-6042" data-mp="2wdi38noorz80">
                  <tbody data-id="element-6043" data-mp="2rdm0errq6du1">
                    {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => {
                    const checked = notes.familyMedicalHistory?.includes(condition) || false;
                    return <tr key={condition} className="border-b border-gray-300" data-id="element-6044" data-mp="3p0urd2ahwf3f">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-6045" data-mp="3k3ujqmxzz4a7">
                            {condition}
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-6046" data-mp="2lpf8xbgheekz">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-6047" data-mp="2qp1ovsnp28xh">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={checked} onChange={e => {
                            const current = notes.familyMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: [...current, condition]
                              });
                            } else {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: current.filter(c => c !== condition)
                              });
                            }
                          }} data-id="element-6048" data-mp="2vgjn48yc1ver" />
                              {checked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-6049" data-mp="1lgfoji1n3u41">
                            <input type="text" placeholder="Notes (e.g. relative, severity)" disabled={!checked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.familyMedicalHistoryNotes?.[condition] || ''} onChange={e => setNotes({
                          ...notes,
                          familyMedicalHistoryNotes: {
                            ...(notes.familyMedicalHistoryNotes || {}),
                            [condition]: e.target.value
                          }
                        })} data-id="element-6050" data-mp="1hgowzc4ah9yg" />
                          </td>
                        </tr>;
                  })}
                    {(() => {
                    const otherChecked = notes.familyMedicalHistory?.includes('OTHER') || false;
                    return <tr className="border-b border-gray-300" data-id="element-6051" data-mp="3lx2hvovq5hnp">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-6052" data-mp="3hxbnlb6zojef">
                            OTHER
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-6053" data-mp="3vqazz4bh5xhr">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-6054" data-mp="3qpvo6h6ucy1w">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={otherChecked} onChange={e => {
                            const current = notes.familyMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: [...current, 'OTHER']
                              });
                            } else {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: current.filter(c => c !== 'OTHER')
                              });
                            }
                          }} data-id="element-6055" data-mp="32zt0ed9yme93" />
                              {otherChecked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-6056" data-mp="3enajbvf37ojp">
                            <input type="text" placeholder="Specify (e.g. condition, relative)" disabled={!otherChecked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.familyMedicalHistoryOther || ''} onChange={e => setNotes({
                          ...notes,
                          familyMedicalHistoryOther: e.target.value
                        })} data-id="element-6057" data-mp="3ep3of6a1y0pl" />
                          </td>
                        </tr>;
                  })()}
                  </tbody>
                </table>
              </div>

              {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
              <div className="border-2 border-black" data-id="element-6058" data-mp="mtrcgtvg58ng">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-6059" data-mp="rq9j5zac7h2f">
                  Obstetrics and Gynaecology History
                </h3>
                <textarea className="w-full p-3 min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.obgynHistory} onChange={e => setNotes({
                ...notes,
                obgynHistory: e.target.value
              })} data-id="element-6060" data-mp="2jskjydsme4fv" />
              </div>

              {/* SOCIAL HISTORY */}
              <div className="border-2 border-black" data-id="element-6061" data-mp="32i0nzb3ikf11">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-6062" data-mp="3giuv7qets9y4">
                  Social History
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-6063" data-mp="3bk9vds3hjsa7">
                  <tbody data-id="element-6064" data-mp="1m40o204o4pds">
                    <tr className="border-b border-gray-300" data-id="element-6065" data-mp="1m5lm3zq6yqge">
                      <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-6066" data-mp="1h68wj77xr5a4">
                        Alcohol
                      </td>
                      <td className="p-2" data-id="element-6067" data-mp="3stm74c506ha9">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.alcohol || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          alcohol: e.target.value
                        }
                      })} data-id="element-6068" data-mp="1mi37q45nb62" />
                      </td>
                    </tr>
                    <tr className="border-b border-gray-300" data-id="element-6069" data-mp="zaa00izawad4">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-6070" data-mp="z8p8714df5iy">
                        Smoking
                      </td>
                      <td className="p-2" data-id="element-6071" data-mp="elr021kgqmbb">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.smoking || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          smoking: e.target.value
                        }
                      })} data-id="element-6072" data-mp="ejeivqmj4fzb" />
                      </td>
                    </tr>
                    <tr className="border-b border-gray-300" data-id="element-6073" data-mp="3840ebh9a8gpw">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-6074" data-mp="383ic83zxv8a2">
                        Occupation
                      </td>
                      <td className="p-2" data-id="element-6075" data-mp="1jknvj6pykclp">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.occupation || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          occupation: e.target.value
                        }
                      })} data-id="element-6076" data-mp="1jjm8e3vo3znm" />
                      </td>
                    </tr>
                    <tr data-id="element-6077" data-mp="1endwr0goiol7">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-6078" data-mp="h5x5kdoq6ktz">
                        Mental Status
                      </td>
                      <td className="p-2" data-id="element-6079" data-mp="m3p43qjvl575">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.mentalStatus || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          mentalStatus: e.target.value
                        }
                      })} data-id="element-6080" data-mp="e4aq1ngh5x5j" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* REVIEW OF SYSTEMS */}
              <div className="border-2 border-black" data-id="element-6081" data-mp="1ghzshcji99yn">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-6082" data-mp="1gkm3xn9io9m5">
                  Review of Systems
                </h3>
                <div className="p-3 space-y-2" data-id="element-6083" data-mp="2tgjnigsgurz1">
                  {[{
                  key: 'rs',
                  label: 'R/S:'
                }, {
                  key: 'cvs',
                  label: 'CVS:'
                }, {
                  key: 'cns',
                  label: 'CNS:'
                }, {
                  key: 'git',
                  label: 'GIT:'
                }, {
                  key: 'gut',
                  label: 'GUT:'
                }, {
                  key: 'mss',
                  label: 'MSS:'
                }, {
                  key: 'is',
                  label: 'IS:'
                }].map(system => <div key={system.key} className="flex items-center" data-id="element-6084" data-mp="4qtijlvax6jg">
                      <span className="font-semibold w-16" data-id="element-6085" data-mp="20tf1scikccsn">{system.label}</span>
                      <input type="text" className="flex-1 p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.reviewOfSystems?.[system.key as keyof typeof notes.reviewOfSystems] || ''} onChange={e => setNotes({
                    ...notes,
                    reviewOfSystems: {
                      ...notes.reviewOfSystems,
                      [system.key]: e.target.value
                    }
                  })} data-id="element-6086" data-mp="1w1n7ps077oxr" />
                    </div>)}
                </div>
              </div>

              {/* ON EXAMINATION */}
              <div className="border-2 border-black" data-id="element-6087" data-mp="361hlraum8ba6">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-6088" data-mp="3a18hlsz01hlk">
                  On Examination
                </h3>
                <div className="p-4 space-y-3" data-id="element-6089" data-mp="3ez1pcqfyajny">
                  {[{
                  key: 'gc' as const,
                  label: 'G/C',
                  helper: 'Stable / Ill Looking / Unconscious'
                }, {
                  key: 'febrile' as const,
                  label: 'Temperature',
                  helper: 'Febrile / Afebrile / Lethargic'
                }, {
                  key: 'pjc' as const,
                  label: 'P | J | C',
                  helper: 'Pallor / Jaundice / Cyanosis'
                }, {
                  key: 's1s2' as const,
                  label: 'S1  S2',
                  helper: 'Heart sounds'
                }, {
                  key: 'rs' as const,
                  label: 'R/S',
                  helper: 'Respiratory system'
                }, {
                  key: 'pa' as const,
                  label: 'P/A',
                  helper: 'Per abdomen'
                }].map(field => <div key={field.key} className="grid grid-cols-[7rem_1fr] gap-3 items-start" data-id="element-6090" data-mp="2avntkfk4w101">
                      <div className="pt-2" data-id="element-6091" data-mp="2ktttwsqakz2s">
                        <p className="font-bold text-sm text-gray-900" data-id="element-6092" data-mp="2ks0jd2ck5upi">
                          {field.label}
                        </p>
                        <p className="text-xs text-gray-500 leading-tight" data-id="element-6093" data-mp="qglsujbq3r8p">
                          {field.helper}
                        </p>
                      </div>
                      <input type="text" value={notes.onExaminationDetails?.[field.key] || ''} onChange={e => setNotes({
                    ...notes,
                    onExaminationDetails: {
                      ...(notes.onExaminationDetails || {
                        gc: '',
                        febrile: '',
                        pjc: '',
                        s1s2: '',
                        rs: '',
                        pa: ''
                      }),
                      [field.key]: e.target.value
                    }
                  })} placeholder={`Enter ${field.label} findings`} className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none" data-id="element-6094" data-mp="vh00w1dbr16q" />
                    </div>)}

                  {/* Additional notes (free text) */}
                  <div className="pt-3 border-t border-gray-200" data-id="element-6095" data-mp="28otoxx3odyqc">
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide" data-id="element-6096" data-mp="1uoscrlhyn4vk">
                      Additional Notes
                    </label>
                    <textarea className="w-full p-2 min-h-[80px] text-sm border-2 border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="Any other examination findings..." value={notes.onExamination} onChange={e => setNotes({
                    ...notes,
                    onExamination: e.target.value
                  })} data-id="element-6097" data-mp="1l0ag9ggprjxq" />
                  </div>
                </div>
              </div>

              {/* SUMMARY OF SIGNIFICANT FINDINGS */}
              <div className="border-2 border-black" data-id="element-6098" data-mp="ife6yxlz3sxi">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-6099" data-mp="igo51mstt145">
                  Summary of Significant Findings
                </h3>
                <div className="p-3 space-y-2" data-id="element-6100" data-mp="sd10oq7xlp1j">
                  {[0, 1, 2, 3, 4, 5, 6, 7].map(i => <div key={i} className="flex items-start" data-id="element-6101" data-mp="16cjvkhbqf3vw">
                      <span className="font-medium mr-2 mt-1" data-id="element-6102" data-mp="16az2kw52ft05">{i + 1}.</span>
                      <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.significantFindings?.[i] || ''} onChange={e => {
                    const current = notes.significantFindings || Array(8).fill('');
                    const updated = [...current];
                    updated[i] = e.target.value;
                    setNotes({
                      ...notes,
                      significantFindings: updated
                    });
                  }} data-id="element-6103" data-mp="11dyukfonog6e" />
                    </div>)}
                </div>
              </div>

              {/* WORKING DIAGNOSIS */}
              <div className="border-2 border-black" data-id="element-6104" data-mp="2vmmacyv8ppul">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-6105" data-mp="2vo73ql5tj2qv">
                  Working Diagnosis
                </h3>
                <div className="p-3 space-y-2" data-id="element-6106" data-mp="35k0lqk2b3og1">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => <div key={i} className="flex items-start" data-id="element-6107" data-mp="39mx1m30gp6ga">
                      <span className="font-medium mr-2 mt-1" data-id="element-6108" data-mp="kub82adca8ox">{i + 1}.</span>
                      <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.workingDiagnosis?.[i] || ''} onChange={e => {
                    const current = notes.workingDiagnosis || Array(10).fill('');
                    const updated = [...current];
                    updated[i] = e.target.value;
                    setNotes({
                      ...notes,
                      workingDiagnosis: updated
                    });
                  }} data-id="element-6109" data-mp="kwdwipyuftrj" />
                    </div>)}
                </div>
              </div>

              {/* INVESTIGATIONS TO BE DONE */}
              <div className="border-2 border-black" data-id="element-6110" data-mp="1cog3d7chhtmk">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-6111" data-mp="3c0ikwc1jsdxl">
                  Investigations To Be Done
                </h3>
                <div className="p-4" data-id="element-6112" data-mp="3727bg2f605fw">
                  {/* LABORATORY TESTS */}
                  <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-6113" data-mp="3q598amigwtid">
                    Laboratory Tests
                  </h4>
                  <table className="w-full border-collapse text-sm mb-4" data-id="element-6114" data-mp="3h5mgmsfjj22i">
                    <tbody data-id="element-6115" data-mp="3h3jexb50zkhy">
                      {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATININE / U&E', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['HORMONAL PROFILE', 'ANTI-MÜLLERIAN HORMONE'], ['RPR', 'HIV'], ['SPERM ANALYSIS', 'TESTOSTERONE'], ['DOA 6', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-6116" data-mp="2lydfpx1wf8xt" data-mpi={idx}>
                          {pair.map((test, ci) => {
                        if (!test) {
                          return <Fragment key={`empty-${ci}`} data-id="element-6117" data-mp="2pxumctt2ac4k">
                                  <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-6118" data-mp="2pydtdfh6bg3t"></td>
                                  <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-6119" data-mp="228t9mud74eth"></td>
                                </Fragment>;
                        }
                        const isOrdered = selectedEncounter.labRequests.some(lab => lab.tests.some(t => t.toLowerCase().includes(test.toLowerCase()) || test.toLowerCase().includes(t.toLowerCase())));
                        return <Fragment key={test} data-id="element-6120" data-mp="12zwpouhb5og7" data-mpi={ci}>
                                <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-6121" data-mp="17xoevgqkiwgp">
                                  {test}
                                </td>
                                <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-6122" data-mp="1gwtdso61krte">
                                  {isOrdered ? <span className="text-green-700 font-bold" data-id="element-6123" data-mp="1lotjyycpggyf">
                                      ✓
                                    </span> : ''}
                                </td>
                              </Fragment>;
                      })}
                        </tr>)}
                    </tbody>
                  </table>

                  {/* RADIOLOGY SCANS */}
                  <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-6124" data-mp="3b4nixvajqru6">
                    Radiology Scans
                  </h4>
                  <table className="w-full border-collapse text-sm mb-4" data-id="element-6125" data-mp="3686vl3b68hi5">
                    <tbody data-id="element-6126" data-mp="7nx6axkglgji">
                      {[['PELVIC SCAN', 'ANOMALY SCAN'], ['OBS SCAN', 'CXR (CHEST X-RAY)'], ['HSG (HYSTEROSALPINGOGRAPHY)', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-6127" data-mp="3p3wx6pf6zbvi" data-mpi={idx}>
                          {pair.map((scan, ci) => {
                        if (!scan) {
                          return <Fragment key={`empty-r-${ci}`} data-id="element-6128" data-mp="lng4axjk3bi3">
                                  <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-6129" data-mp="1ncle4pc8w4eq"></td>
                                  <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-6130" data-mp="1iea68k5ig3z5"></td>
                                </Fragment>;
                        }
                        const isOrdered = selectedEncounter.radiologyRequests.some(r => {
                          const a = r.scanType.toLowerCase();
                          const b = scan.toLowerCase();
                          return a.includes(b) || b.includes(a) || b.includes('cxr') && a.includes('cxr') || b.includes('hsg') && a.includes('hsg');
                        });
                        return <Fragment key={scan} data-id="element-6131" data-mp="2k4yr3j8ytrhj" data-mpi={ci}>
                                <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-6132" data-mp="33onubpclgvbv">
                                  {scan}
                                </td>
                                <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-6133" data-mp="38lwaxli3grb0">
                                  {isOrdered ? <span className="text-green-700 font-bold" data-id="element-6134" data-mp="2yp1d74tnauxs">
                                      ✓
                                    </span> : ''}
                                </td>
                              </Fragment>;
                      })}
                        </tr>)}
                    </tbody>
                  </table>

                  {/* ─── PENDING INVESTIGATIONS — RECALL ─── */}
                  {(() => {
                  const pendingLabs = selectedEncounter.labRequests.filter(l => l.status === 'Pending');
                  const pendingRads = selectedEncounter.radiologyRequests.filter(r => r.status === 'Pending');
                  const hasAny = pendingLabs.some(l => l.tests.length > 0 || l.otherTests.trim().length) || pendingRads.length > 0;
                  if (!hasAny) return null;
                  return <div className="mb-4 border border-amber-300 bg-amber-50 rounded-md p-3" data-id="element-6135" data-mp="qgd3jq57re3m">
                        <h4 className="font-bold text-xs uppercase text-amber-800 mb-2" data-id="element-6136" data-mp="mf1djjifgquw">
                          Pending Investigations (Recall to make changes)
                        </h4>
                        <ul className="space-y-1.5 text-sm" data-id="element-6137" data-mp="378ejkp13smz5">
                          {pendingLabs.flatMap(lab => lab.tests.map(testName => <li key={`${lab.id}-${testName}`} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-6138" data-mp="1hr9730k0lpz3">
                                <span data-id="element-6139" data-mp="1vh4alj03bk5f">
                                  <span className="font-medium" data-id="element-6140" data-mp="1vi5zaqa1455m">
                                    {testName}
                                  </span>{' '}
                                  <span className="text-xs text-gray-500" data-id="element-6141" data-mp="25fabbxa5uk8p">
                                    · Laboratory ·{' '}
                                    {new Date(lab.requestedAt).toLocaleTimeString()}
                                  </span>
                                </span>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallLabTest(lab.id, testName)} data-id="element-6142" data-mp="g2p0ndc34jv8">
                                  <RotateCcw className="w-3 h-3 mr-1" data-id="element-6143" data-mp="3pwrlvbgf9uja" /> Recall
                                </Button>
                              </li>))}
                          {pendingLabs.filter(l => l.otherTests.trim().length > 0).map(lab => <li key={`${lab.id}-other`} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-6144" data-mp="3bytlxb2um0nb">
                                <span data-id="element-6145" data-mp="2ei4ixe25wscc">
                                  <span className="font-medium" data-id="element-6146" data-mp="2ef8o3ylm2js4">
                                    {lab.otherTests}
                                  </span>{' '}
                                  <span className="text-xs text-gray-500" data-id="element-6147" data-mp="yr2bwp9dt1ri">
                                    · Other lab test
                                  </span>
                                </span>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallLabTest(lab.id, lab.otherTests, true)} data-id="element-6148" data-mp="p10olzourndn">
                                  <RotateCcw className="w-3 h-3 mr-1" data-id="element-6149" data-mp="3tfgw5vksofjk" /> Recall
                                </Button>
                              </li>)}
                          {pendingRads.map(rad => <li key={rad.id} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-6150" data-mp="1u179z6r1rfjl">
                              <span data-id="element-6151" data-mp="1g7fxbpi0rfjn">
                                <span className="font-medium" data-id="element-6152" data-mp="1g8iswaqae65w">
                                  {rad.scanType}
                                </span>{' '}
                                <span className="text-xs text-gray-500" data-id="element-6153" data-mp="1699y5fnlknih">
                                  · Radiology ·{' '}
                                  {new Date(rad.requestedAt).toLocaleTimeString()}
                                </span>
                              </span>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallRadiology(rad.id)} data-id="element-6154" data-mp="2wmqzgvunw4vw">
                                <RotateCcw className="w-3 h-3 mr-1" data-id="element-6155" data-mp="w5d44e2w2exz" /> Recall
                              </Button>
                            </li>)}
                        </ul>
                        <p className="text-xs text-amber-700 mt-2 italic" data-id="element-6156" data-mp="4e5jvhztwf4p">
                          Recall removes the request before the lab or radiology
                          department processes it. Once a test is completed, it
                          becomes part of the permanent record.
                        </p>
                      </div>;
                })()}

                  <div className="flex gap-3 mb-4" data-id="element-6157" data-mp="1ou0jjt0yia3m">
                    <Button variant="outline" onClick={() => setShowLabModal(true)} className="flex-1" data-id="element-6158" data-mp="1osphpyr2ncf5">
                      <FlaskConical className="w-4 h-4 mr-2" data-id="element-6159" data-mp="vk6sjogrjnc1" /> Request
                      Laboratory Test
                    </Button>
                    <Button variant="outline" onClick={() => setShowRadModal(true)} className="flex-1 border-purple-600 text-purple-600 hover:bg-purple-50" data-id="element-6160" data-mp="19bv0dfjzbpn7">
                      <Scan className="w-4 h-4 mr-2" data-id="element-6161" data-mp="1e820ustlw8xr" /> Request Radiology Scan
                    </Button>
                  </div>

                  <div className="border-t border-gray-300 pt-2" data-id="element-6162" data-mp="1s839iug44kns">
                    <span className="font-semibold" data-id="element-6163" data-mp="1o8cf8grdnmei">Other investigations:</span>{' '}
                    {selectedEncounter.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                  </div>

                  {/* Investigation Results — View File per test */}
                  {(selectedEncounter.labRequests.some(l => l.status === 'Completed') || selectedEncounter.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-6164" data-mp="3b1we3xvjfybw">
                      <h4 className="font-bold mb-3 uppercase text-center" data-id="element-6165" data-mp="2w5yo6xc7jq84">
                        Investigation Results
                      </h4>
                      <table className="w-full border-collapse text-sm" data-id="element-6166" data-mp="2n61ughayo167">
                        <thead data-id="element-6167" data-mp="2n84snom9p0m0">
                          <tr className="bg-gray-100" data-id="element-6168" data-mp="2s4v6046oti3q">
                            <th className="border border-black p-1.5 text-left" data-id="element-6169" data-mp="2s5er1trmwmlz">
                              Test / Scan
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-6170" data-mp="qebbqr6sujv3">
                              Type
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-6171" data-mp="ej20cli4nb8l">
                              Performed By
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-6172" data-mp="5qrrb7iyxbsx">
                              Completed
                            </th>
                            <th className="border border-black p-1.5 text-center w-32" data-id="element-6173" data-mp="9p7at1fuc21l">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody data-id="element-6174" data-mp="3iwru3fkc9vp2">
                          {selectedEncounter.labRequests.filter(l => l.status === 'Completed').flatMap(lab => lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).map(testName => <tr key={`${lab.id}-${testName}`} data-id="element-6175" data-mp="31nft9qkd0r3s">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-6176" data-mp="31l34atg6pgh8">
                                      {testName}
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-6177" data-mp="3amb2zcmm4jbu">
                                      Laboratory
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-6178" data-mp="3kegqa91x6ckp">
                                      —
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-6179" data-mp="25ol6cc3dak1">
                                      {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center" data-id="element-6180" data-mp="7w9dhq1euwew">
                                      <div className="flex justify-center gap-2" data-id="element-6181" data-mp="7y3yt5glgqji">
                                        <Button size="sm" variant="outline" onClick={() => setViewingTest({
                              kind: 'lab',
                              labId: lab.id,
                              testName
                            })} data-id="element-6182" data-mp="cu2lmom9y0ht">
                                          <Eye className="w-3.5 h-3.5 mr-1" data-id="element-6183" data-mp="14o4mt29532zr" />{' '}
                                          View File
                                        </Button>
                                        <button onClick={() => setRemoveTestModal({
                              isOpen: true,
                              labId: lab.id,
                              testName,
                              reason: ''
                            })} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors print:hidden" title="Remove duplicate test" data-id="element-6184" data-mp="2u4gmp94l8g9r">
                                          <Trash2 className="w-4 h-4" data-id="element-6185" data-mp="crtkbk984ht" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>))}
                          {selectedEncounter.labRequests.filter(l => l.status === 'Completed' && l.otherTests && !(l.removedTestNames || []).includes(l.otherTests)).map(lab => <tr key={`${lab.id}-other`} data-id="element-6186" data-mp="2vbr3tcnm019">
                                <td className="border border-black p-1.5 font-medium" data-id="element-6187" data-mp="3l91doll2fg9k">
                                  {lab.otherTests}{' '}
                                  <span className="text-xs text-gray-500" data-id="element-6188" data-mp="3q72u0yr3x1hc">
                                    (Other)
                                  </span>
                                </td>
                                <td className="border border-black p-1.5" data-id="element-6189" data-mp="mm6x2krolhrf">
                                  Laboratory
                                </td>
                                <td className="border border-black p-1.5" data-id="element-6190" data-mp="rw9cuyfk03k8">—</td>
                                <td className="border border-black p-1.5 text-xs" data-id="element-6191" data-mp="15p8qsvzotppo">
                                  {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                </td>
                                <td className="border border-black p-1.5 text-center" data-id="element-6192" data-mp="8yq1ho5mi8un">
                                  <div className="flex justify-center gap-2" data-id="element-6193" data-mp="my7vqj1ouebe">
                                    <Button size="sm" variant="outline" onClick={() => setViewingTest({
                              kind: 'lab',
                              labId: lab.id,
                              testName: lab.otherTests
                            })} data-id="element-6194" data-mp="mw4vl66tn4sy">
                                      <Eye className="w-3.5 h-3.5 mr-1" data-id="element-6195" data-mp="2agpeq9pudbtw" /> View
                                      File
                                    </Button>
                                    <button onClick={() => setRemoveTestModal({
                              isOpen: true,
                              labId: lab.id,
                              testName: lab.otherTests,
                              reason: ''
                            })} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors print:hidden" title="Remove duplicate test" data-id="element-6196" data-mp="1hra1reem98vc">
                                      <Trash2 className="w-4 h-4" data-id="element-6197" data-mp="rgq1v54aqzex" />
                                    </button>
                                  </div>
                                </td>
                              </tr>)}
                          {selectedEncounter.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <tr key={rad.id} data-id="element-6198" data-mp="8ox8h9lbfc6o">
                                <td className="border border-black p-1.5 font-medium" data-id="element-6199" data-mp="3r5hqiwepw42">
                                  {rad.scanType}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-6200" data-mp="3uvvjkc45qpr7">
                                  Radiology
                                </td>
                                <td className="border border-black p-1.5" data-id="element-6201" data-mp="3ly37dgtqfy81">
                                  {rad.radiographerName || '—'}
                                </td>
                                <td className="border border-black p-1.5 text-xs" data-id="element-6202" data-mp="1inyg8knz1uv5">
                                  {rad.completedAt ? new Date(rad.completedAt).toLocaleString() : ''}
                                </td>
                                <td className="border border-black p-1.5 text-center" data-id="element-6203" data-mp="uxav2xp1p8qn">
                                  <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'rad',
                            radId: rad.id
                          })} data-id="element-6204" data-mp="zoaqtzmfhkrk">
                                    <Eye className="w-3.5 h-3.5 mr-1" data-id="element-6205" data-mp="g2o5fmzybq98" /> View
                                    File
                                  </Button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>}
                </div>
              </div>

              {/* DRUG TREATMENT / PRESCRIPTION */}
              <div className="border-2 border-black" data-id="element-6206" data-mp="gac93i8yj6jb">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-6207" data-mp="g8sqhkztwjed">
                  Drug Treatment / Prescription
                  {selectedEncounter.prescriptions.length > 0 && <span className="ml-2 text-xs font-normal text-gray-600 lowercase" data-id="element-6208" data-mp="l240fuzxtgai">
                      ({selectedEncounter.prescriptions.length} of 20 drugs)
                    </span>}
                </h3>
                <div className="max-h-[420px] overflow-y-auto" data-id="element-6209" data-mp="u3lez8xp59x3">
                  <table className="w-full border-collapse text-sm text-center" data-id="element-6210" data-mp="mjd4nnc5zl5v">
                    <thead data-id="element-6211" data-mp="mhaf14exbu1o">
                      <tr className="bg-gray-100" data-id="element-6212" data-mp="ihi2yhqbn5rk">
                        <th className="border border-black p-1.5 w-10" data-id="element-6213" data-mp="ifxbj0zb02xx">No.</th>
                        <th className="border border-black p-1.5 text-left" data-id="element-6214" data-mp="dhdn8tx8mf2e">
                          Drug Name
                        </th>
                        <th className="border border-black p-1.5" data-id="element-6215" data-mp="3kr5ie07k44l">Dose</th>
                        <th className="border border-black p-1.5" data-id="element-6216" data-mp="3mvhzkc6abgh">Route</th>
                        <th className="border border-black p-1.5" data-id="element-6217" data-mp="3uyn5ks4bikc2">Frequency</th>
                        <th className="border border-black p-1.5" data-id="element-6218" data-mp="3qx1sb5nvg41c">
                          No. of Days
                        </th>
                        <th className="border border-black p-1.5" data-id="element-6219" data-mp="3m0l8gnnpgbu2">Signature</th>
                        <th className="border border-black p-1.5 w-16" data-id="element-6220" data-mp="3gycd5knd0xos">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-6221" data-mp="31vm1qfk8udyu">
                      {selectedEncounter.prescriptions.length > 0 ? selectedEncounter.prescriptions.map((rx, i) => <tr key={rx.id} className={rx.status === 'Dispensed' ? 'bg-green-50' : rx.status === 'Prescribed' || rx.status === 'Not Dispensed' ? 'bg-amber-50' : ''} data-id="element-6222" data-mp="3bv37sy3eqtvc" data-mpi={i}>
                              <td className="border border-black p-1.5" data-id="element-6223" data-mp="2xvagp1nyybmu">
                                {i + 1}
                              </td>
                              <td className="border border-black p-1.5 text-left font-medium" data-id="element-6224" data-mp="20dk8vhwv1d2l">
                                {rx.drugName}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-6225" data-mp="1xch2biwa83g1">
                                {rx.dose}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-6226" data-mp="273lhailyjoav">
                                {rx.route}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-6227" data-mp="225a88a3hlhtp">
                                {rx.frequency}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-6228" data-mp="19gcribexbt32">
                                {rx.days}
                              </td>
                              <td className="border border-black p-1.5 text-xs" data-id="element-6229" data-mp="1oeolubetqpr2">
                                {rx.status === 'Dispensed' && <span className="text-green-700 font-bold" data-id="element-6230" data-mp="2zsack10c0zep">
                                    ✓ Dispensed
                                  </span>}
                                {(rx.status === 'Prescribed' || rx.status === 'Not Dispensed') && <span className="font-bold text-amber-700" data-id="element-6231" data-mp="37culfhv93jp2">
                                    ℞ Prescribed — not in stock
                                  </span>}
                                {rx.status === 'Pending' && <span className="italic text-gray-500" data-id="element-6232" data-mp="3l36g9oj7zwjw">
                                    Pending
                                  </span>}
                                {rx.pharmacistName && <div className="text-gray-500 italic mt-0.5" data-id="element-6233" data-mp="3c91rdanncv70">
                                    {rx.pharmacistName}
                                  </div>}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-6234" data-mp="cnwhapamn7gc">
                                {rx.status === 'Pending' ? <button onClick={() => handleDeletePrescription(rx.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors" title="Remove this drug" aria-label={`Remove ${rx.drugName}`} data-id="element-6235" data-mp="2l60k5ys1aodq">
                                    <Trash2 className="w-4 h-4" data-id="element-6236" data-mp="2ywngjkabo26n" />
                                  </button> : <span className="text-gray-300" data-id="element-6237" data-mp="2q4204au5y7kz">—</span>}
                              </td>
                            </tr>) : Array.from({
                      length: 20
                    }, (_, idx) => idx + 1).map(i => <tr key={i} data-id="element-6238" data-mp="19c7zy0pk1rcm">
                              <td className="border border-black p-3" data-id="element-6239" data-mp="19d9nv5ro68br">{i}</td>
                              <td className="border border-black p-3" data-id="element-6240" data-mp="1dbp5sv8w8qib"></td>
                              <td className="border border-black p-3" data-id="element-6241" data-mp="1id69eyirir2l"></td>
                              <td className="border border-black p-3" data-id="element-6242" data-mp="1nbpvcyxcw2uy"></td>
                              <td className="border border-black p-3" data-id="element-6243" data-mp="1saav6x8p4kiv"></td>
                              <td className="border border-black p-3" data-id="element-6244" data-mp="1s8gb1l590wfu"></td>
                              <td className="border border-black p-3" data-id="element-6245" data-mp="1x2aq2b9wsn8l"></td>
                              <td className="border border-black p-3" data-id="element-6246" data-mp="2134gbbatqe4n"></td>
                            </tr>)}
                    </tbody>
                  </table>
                </div>
                <div className="p-3 border-t border-gray-300 flex gap-3" data-id="element-6247" data-mp="2azzbplbsvyep">
                  <Button variant="outline" onClick={() => setShowRxModal(true)} className="flex-1 text-green-600 border-green-600 hover:bg-green-50" disabled={selectedEncounter.prescriptions.length >= 20} data-id="element-6248" data-mp="ayj0ochl1h3c">
                    <Pill className="w-4 h-4 mr-2" data-id="element-6249" data-mp="fxvuxias9qfy" />
                    {selectedEncounter.prescriptions.length >= 20 ? 'Maximum 20 Drugs Reached' : 'Prescribe Medication'}
                  </Button>
                  {selectedEncounter.prescriptions.some(rx => rx.status === 'Pending') && <Button onClick={handleSendToPharmacy} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-id="element-6250" data-mp="13ku6q2nyka7j">
                      <Pill className="w-4 h-4 mr-2" data-id="element-6251" data-mp="35gnvxidxpjet" /> Send Prescription to
                      Pharmacy
                    </Button>}
                </div>
              </div>

              {/* FOLLOW UP / REVIEW DATE */}
              <div className="border-2 border-black" data-id="element-6252" data-mp="1kx2axvik0von">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-6253" data-mp="1kylnbb13aml5">
                  Next Follow Up / Review Date (If Any)
                </h3>
                <table className="w-full border-collapse text-sm text-center" data-id="element-6254" data-mp="1b54w8esc7ll6">
                  <thead data-id="element-6255" data-mp="x5wqf31o5f80">
                    <tr className="bg-gray-100" data-id="element-6256" data-mp="x5n2rm43jk5h">
                      <th className="border border-black p-1 w-10" data-id="element-6257" data-mp="126kizt5jq44c">No.</th>
                      <th className="border border-black p-1" data-id="element-6258" data-mp="n8s4937tpjr7">Date</th>
                      <th className="border border-black p-1 w-10" data-id="element-6259" data-mp="nalcuo1zye1u">No.</th>
                      <th className="border border-black p-1" data-id="element-6260" data-mp="s8wp15frl0n8">Date</th>
                      <th className="border border-black p-1 w-10" data-id="element-6261" data-mp="2hm9vb07fsyqz">No.</th>
                      <th className="border border-black p-1" data-id="element-6262" data-mp="2hkyth5xjy12i">Date</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-6263" data-mp="1ac9fgl9jh7jg">
                    {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-6264" data-mp="15dg3ix9whole">
                        <td className="border border-black p-1 font-medium" data-id="element-6265" data-mp="15d6epd0ldnha">
                          {row + 1}
                        </td>
                        <td className="border border-black p-1" data-id="element-6266" data-mp="10kmzkbm9akad">
                          {row === 0 ? <input type="date" className="w-full p-1 text-center focus:ring-2 focus:ring-blue-500 focus:outline-none" value={selectedEncounter.reviewDate ? new Date(selectedEncounter.reviewDate).toISOString().split('T')[0] : ''} onChange={e => {
                        if (e.target.value) {
                          updateEncounter(selectedEncounter.id, {
                            reviewDate: new Date(e.target.value).toISOString()
                          });
                        }
                      }} data-id="element-6267" data-mp="vlshjbw44zrb" /> : ''}
                        </td>
                        <td className="border border-black p-1 font-medium" data-id="element-6268" data-mp="20lqf3w0nc27a">
                          {row + 6}
                        </td>
                        <td className="border border-black p-2" data-id="element-6269" data-mp="25kbgvzvjqtxu"></td>
                        <td className="border border-black p-1 font-medium" data-id="element-6270" data-mp="2alqzlla94od2">
                          {row + 11}
                        </td>
                        <td className="border border-black p-2" data-id="element-6271" data-mp="3h2w4wofy2xrb"></td>
                      </tr>)}
                  </tbody>
                </table>
                <div className="border-t border-black p-3" data-id="element-6272" data-mp="183hb3798lqtt">
                  <h4 className="font-bold uppercase text-sm mb-2" data-id="element-6273" data-mp="135yxv4o6spg0">Notes</h4>
                  <textarea className="w-full p-2 border border-gray-300 rounded text-sm min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:outline-none" placeholder="Add any notes related to the follow-up or review (e.g. reason for review, instructions to patient, expected investigations)…" value={reviewDateNotes} onChange={e => setReviewDateNotes(e.target.value)} data-id="element-6274" data-mp="133ux2vll46jy" />
                </div>
              </div>

              {/* NAME OF MEDICAL PRACTITIONER */}
              <div className="border-2 border-black p-4 mt-6" data-id="element-6275" data-mp="u47sk0xk6kmo">
                <p className="font-bold text-sm" data-id="element-6276" data-mp="29l8azegdaz3l">
                  Name of the Medical Practitioner:{' '}
                  <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-6277" data-mp="25kxvnb6oqwc2">
                    {selectedEncounter.clinicalNote?.clinicianName || currentUser?.fullName || ''}
                  </span>
                </p>
              </div>
            </div>
          </fieldset>

          {/* VERSION HISTORY VIEWER */}
          {selectedEncounter.clinicalNote?.editHistory && selectedEncounter.clinicalNote.editHistory.length > 0 && <div className="mt-8 border-t-2 border-dashed border-gray-300 pt-6 print:hidden" data-id="element-6278" data-mp="2vvhr989z2jmo">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center" data-id="element-6279" data-mp="30ryhq94n9a2x">
                  <History className="w-5 h-5 mr-2 text-gray-500" data-id="element-6280" data-mp="2m3c3ybuo0qdt" />
                  Version History
                </h3>
                <div className="space-y-4" data-id="element-6281" data-mp="2r1vr2fkzw87r">
                  {selectedEncounter.clinicalNote.editHistory.map((edit, idx) => <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-6282" data-mp="3jkat1ld9zlp7" data-mpi={idx}>
                        <div className="flex justify-between items-start mb-2" data-id="element-6283" data-mp="3tddogjffj3be">
                          <div data-id="element-6284" data-mp="3ohg5e5cs7iux">
                            <p className="font-semibold text-gray-900" data-id="element-6285" data-mp="3kf1r7yk2579z">
                              Edited by {edit.editedByName}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-6286" data-mp="3ffp2t9djfs5a">
                              {new Date(edit.editedAt).toLocaleString()}
                            </p>
                          </div>
                          <Badge variant="warning" data-id="element-6287" data-mp="35oug63wdqqjb">Version {idx + 1}</Badge>
                        </div>
                        <div className="bg-white border border-gray-100 p-3 rounded text-sm text-gray-700 mt-2" data-id="element-6288" data-mp="30ozpa0clu0y9">
                          <span className="font-semibold text-gray-900" data-id="element-6289" data-mp="2wnnvrjqm0vks">
                            Reason for change:
                          </span>{' '}
                          {edit.reason}
                        </div>
                        <details className="mt-3" data-id="element-6290" data-mp="129s73j0u7n4l">
                          <summary className="text-sm text-blue-600 cursor-pointer hover:underline font-medium" data-id="element-6291" data-mp="1290hqpxu2lmr">
                            View previous note content
                          </summary>
                          <div className="mt-2 p-3 bg-white border border-gray-200 rounded text-xs text-gray-600 max-h-60 overflow-y-auto font-mono whitespace-pre-wrap" data-id="element-6292" data-mp="1c5bypt8dbznj">
                            {JSON.stringify(edit.previousNote, null, 2)}
                          </div>
                        </details>
                      </div>)}
                </div>
              </div>}
        </div>

        {/* ═══ MODALS ═══ */}
        {removeTestModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-6293" data-mp="xc0fyr80o51g">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-6294" data-mp="4tv3l7wvcwp6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center text-red-600" data-id="element-6295" data-mp="4rs13oej5b3k">
                <Trash2 className="w-5 h-5 mr-2" data-id="element-6296" data-mp="3hsji51xdkn5p" /> Remove Duplicate Test
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-6297" data-mp="3cxdspt4rkqcv">
                Why are you removing "{removeTestModal.testName}"? This action
                will be logged in the audit trail.
              </p>
              <div className="mb-4" data-id="element-6298" data-mp="3mrnpdn0qoate">
                <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none" rows={3} value={removeTestModal.reason} onChange={e => setRemoveTestModal({
              ...removeTestModal,
              reason: e.target.value
            })} placeholder="e.g., Duplicate entry, entered by mistake..." data-id="element-6299" data-mp="4f7yjdbpjqok" />
              </div>
              <div className="flex justify-end space-x-3" data-id="element-6300" data-mp="2b8a4cp76ovuz">
                <Button variant="outline" onClick={() => setRemoveTestModal({
              isOpen: false,
              labId: '',
              testName: '',
              reason: ''
            })} data-id="element-6301" data-mp="qp7o1hcg100w">
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleRemoveTestConfirm} disabled={!removeTestModal.reason.trim()} data-id="element-6302" data-mp="7x5d8mmhgdyk">
                  Confirm Removal
                </Button>
              </div>
            </div>
          </div>}

        {/* Lab Modal */}
        {showLabModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-6303" data-mp="30f12adsu2if">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col" data-id="element-6304" data-mp="1xclc996p0jaq">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-6305" data-mp="1vvfzk0ueuyv8">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-6306" data-mp="1qwmk42vkcxsf">
                  Request Laboratory Tests
                </h3>
                <button onClick={() => setShowLabModal(false)} data-id="element-6307" data-mp="1lybb1ud3erb9">
                  <X className="w-5 h-5 text-gray-400" data-id="element-6308" data-mp="1gy6uy5g3jylk" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1" data-id="element-6309" data-mp="2elxlnmkqmw7m">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-id="element-6310" data-mp="2el5seiem5aki">
                  <div data-id="element-6311" data-mp="29nvvyist3yn6">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-6312" data-mp="29nmfbltnj3u5">
                      HAEMATOLOGY
                    </h4>
                    {['FBC', 'Blood Group', 'CD4 Count', 'MPS/RDT', 'AAFB', 'Viral Load'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-6313" data-mp="2sk5ebiaqrbcz">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-6314" data-mp="34veloyfrbsvz" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-6315" data-mp="6eu4zt2z2q97">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-6316" data-mp="6flq29yy15l8">
                      CHEMISTRY
                    </h4>
                    {['Urea', 'Creatinine', 'U&E', 'FBS', 'RBS', 'Liver Function Test', 'VBG', 'ABG'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-6317" data-mp="10pxjx39amsns">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-6318" data-mp="d0w7l4xcaxd8" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-6319" data-mp="25qezt57e2lib">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-6320" data-mp="25qosx1nqd8s8">
                      SEROLOGY
                    </h4>
                    {['HIV', 'Syphilis', 'RPR', 'HBs Ag', 'HCV', 'Hepatitis Profile', 'Gravindex (Pregnancy Test)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-6321" data-mp="2aoq9nfxoow0j">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-6322" data-mp="2fouu1h1pqaw1" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-6323" data-mp="tweb4fm4wvtv">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-6324" data-mp="yx2abokioiz6">
                      ENDOCRINOLOGY
                    </h4>
                    {['Hormonal Profile', 'Testosterone', 'Anti-Müllerian Hormone (AMH)', 'Beta HCG (Quantitative)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-6325" data-mp="g6ufruuvv0ul">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-6326" data-mp="2tfsres9ovryr" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-6327" data-mp="1wsefn8ezcj72">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-6328" data-mp="1woqmwbhd00te">
                      URINE / FERTILITY
                    </h4>
                    {['Urinalysis', 'Urine Microscopy', 'Urine Culture', 'Sperm Analysis', 'Sperm Culture', 'PAP Smear'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-6329" data-mp="144v66ji17wzw">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-6330" data-mp="1868b8rdavw5r" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-6331" data-mp="841w8aniv3hb">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-6332" data-mp="371ozwexruom">
                      MICROBIOLOGY / SWABS
                    </h4>
                    {['HVS', 'Urethral Swab', 'Stool Microscopy'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-6333" data-mp="cxd66knms0dg">
                          <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-6334" data-mp="3ge3fb8eietjd" />{' '}
                          {test}
                        </label>)}
                  </div>
                  <div data-id="element-6335" data-mp="3k969ume12g1d">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-6336" data-mp="3p9kia5jjjrzx">
                      OTHER
                    </h4>
                    {['DOA 6 (Drugs of Abuse)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-6337" data-mp="32dubb4kt947">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-6338" data-mp="7udn8jm8b1ra" />{' '}
                        {test}
                      </label>)}
                  </div>
                </div>
                <div className="mt-6" data-id="element-6339" data-mp="hyu5tkr3ewys">
                  <Input label="Other Tests (Free Text)" value={otherTests} onChange={e => setOtherTests(e.target.value)} placeholder="Enter any other tests required..." data-id="element-6340" data-mp="1entcivc13qov" />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-6341" data-mp="10wol2gite4pm">
                <Button variant="outline" onClick={() => setShowLabModal(false)} data-id="element-6342" data-mp="1xet7qea49wql">
                  Cancel
                </Button>
                <Button onClick={handleRequestLab} data-id="element-6343" data-mp="3muddzfvj3mcy">Send to Laboratory</Button>
              </div>
            </div>
          </div>}

        {/* Rad Modal */}
        {showRadModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-6344" data-mp="3d5n31eiigo1t">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-6345" data-mp="3i3ono3vl4vfe">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-6346" data-mp="344f5wzrqdt9b">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-6347" data-mp="3938jstaxjma0">
                  Request Radiology Scan
                </h3>
                <button onClick={() => setShowRadModal(false)} data-id="element-6348" data-mp="2u7asbobyb644">
                  <X className="w-5 h-5 text-gray-400" data-id="element-6349" data-mp="2z5ccydp0zdhp" />
                </button>
              </div>
              <div className="p-6" data-id="element-6350" data-mp="seuxatkhizt7">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-6351" data-mp="sfxqx996zgct">
                  Scan Type <span className="text-red-500" data-id="element-6352" data-mp="xe7ht7nvvusm">*</span>
                </label>
                <select required value={scanType} onChange={e => setScanType(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm" data-id="element-6353" data-mp="2hvqgakhbl7it">
                  <option value="" data-id="element-6354" data-mp="2mtiajl5ft5q6">— Select a scan —</option>
                  <option value="Pelvic Scan" data-id="element-6355" data-mp="30lyfwslt9fu9">Pelvic Scan</option>
                  <option value="Anomaly Scan" data-id="element-6356" data-mp="30ocaz1ghqs1t">Anomaly Scan</option>
                  <option value="OBS Scan" data-id="element-6357" data-mp="2wkcwspioo5bm">OBS Scan</option>
                  <option value="Abdominal Scan" data-id="element-6358" data-mp="3al77jet7eidg">Abdominal Scan</option>
                  <option value="Abdomen/Pelvis Scan" data-id="element-6359" data-mp="3aib81m1pjlmw">
                    Abdomen and Pelvic Scan (Abdomen/Pelvis Scan)
                  </option>
                  <option value="CXR (Chest X-Ray)" data-id="element-6360" data-mp="1g7ph9k1ez7eq">CXR (Chest X-Ray)</option>
                  <option value="HSG (Hysterosalpingography)" data-id="element-6361" data-mp="1b832v79unl4c">
                    HSG (Hysterosalpingography)
                  </option>
                </select>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-6362" data-mp="udh22qif8su3">
                <Button variant="outline" onClick={() => setShowRadModal(false)} data-id="element-6363" data-mp="z4gzdwvgdcx4">
                  Cancel
                </Button>
                <Button onClick={handleRequestRad} className="bg-purple-600 hover:bg-purple-700" data-id="element-6364" data-mp="1454ogd04fjop">
                  Send to Radiology
                </Button>
              </div>
            </div>
          </div>}

        {/* Rx Modal */}
        {showRxModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 overflow-y-auto" data-id="element-6365" data-mp="ldbx0n0pa6j3">
            <div className="min-h-full flex items-start sm:items-center justify-center p-4" data-id="element-6366" data-mp="1rt4ok3jbomfy">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[calc(100vh-2rem)] flex flex-col my-4" data-id="element-6367" data-mp="1wrpffccoptrq">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 sticky top-0 bg-white rounded-t-lg z-10" data-id="element-6368" data-mp="3ha9rb0am052t">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-6369" data-mp="3cd9kgn5xqyan">
                    Prescribe Medication
                  </h3>
                  <button onClick={() => setShowRxModal(false)} data-id="element-6370" data-mp="32llukqp11pba">
                    <X className="w-5 h-5 text-gray-400" data-id="element-6371" data-mp="32j9giome3z3i" />
                  </button>
                </div>
                <div className="p-6 overflow-y-auto flex-1" data-id="element-6372" data-mp="2tkfcwean68gg">
                  <div className="grid grid-cols-2 gap-4" data-id="element-6373" data-mp="2tiukovbsv1lr">
                    <Input label="Drug Name" className="col-span-2" value={rxForm.drugName} onChange={e => setRxForm({
                  ...rxForm,
                  drugName: e.target.value
                })} required data-id="element-6374" data-mp="2okt189agp09r" />
                    <Input label="Dose" placeholder="e.g. 500mg" value={rxForm.dose} onChange={e => setRxForm({
                  ...rxForm,
                  dose: e.target.value
                })} required data-id="element-6375" data-mp="19nmlaqwk7ceh" />
                    <Select label="Route" options={[{
                  value: 'PO',
                  label: 'Oral (PO)'
                }, {
                  value: 'IV',
                  label: 'Intravenous (IV)'
                }, {
                  value: 'IM',
                  label: 'Intramuscular (IM)'
                }, {
                  value: 'Topical',
                  label: 'Topical'
                }, {
                  value: 'Vaginal',
                  label: 'Vaginal'
                }, {
                  value: 'Rectal',
                  label: 'Rectal'
                }]} value={rxForm.route} onChange={e => setRxForm({
                  ...rxForm,
                  route: e.target.value
                })} data-id="element-6376" data-mp="r011f5p44pwc" />
                    <Input label="Frequency" placeholder="e.g. BD, TDS" value={rxForm.frequency} onChange={e => setRxForm({
                  ...rxForm,
                  frequency: e.target.value
                })} required data-id="element-6377" data-mp="29xnm74b6rbkj" />
                    <Input label="No. of Days" type="number" value={rxForm.days} onChange={e => setRxForm({
                  ...rxForm,
                  days: e.target.value
                })} required data-id="element-6378" data-mp="2qo9c8nt48474" />
                  </div>
                  <div className="mt-4 flex justify-end" data-id="element-6379" data-mp="27wgn54h6334o">
                    <Button onClick={handleAddRx} variant="outline" className="text-green-600 border-green-600 hover:bg-green-50" data-id="element-6380" data-mp="2moioksyvy46t">
                      + Add to Prescription List
                    </Button>
                  </div>

                  {selectedEncounter.prescriptions.length > 0 && <div className="mt-6 border-t border-gray-200 pt-4" data-id="element-6381" data-mp="4prewu4x094t">
                      <div className="flex justify-between items-center mb-2" data-id="element-6382" data-mp="9ghhjzlyhxvt">
                        <h4 className="font-medium text-sm" data-id="element-6383" data-mp="9iki3dkqj9es">
                          Current List ({selectedEncounter.prescriptions.length}{' '}
                          of 20)
                        </h4>
                        {selectedEncounter.prescriptions.length >= 20 && <span className="text-xs text-amber-600 font-medium" data-id="element-6384" data-mp="neftkyct1i85">
                            Maximum reached
                          </span>}
                      </div>
                      <ul className="space-y-2 max-h-64 overflow-y-auto pr-1 border border-gray-200 rounded p-2 bg-gray-50" data-id="element-6385" data-mp="ei6tdhh06fky">
                        {selectedEncounter.prescriptions.map((rx, idx) => <li key={rx.id} className="text-sm bg-white p-2 rounded flex justify-between items-center border border-gray-100 gap-2" data-id="element-6386" data-mp="2z8fpra3g2nr5" data-mpi={idx}>
                            <span className="flex-1 min-w-0 truncate" data-id="element-6387" data-mp="3enkv0urf6sok">
                              <span className="text-gray-400 mr-2" data-id="element-6388" data-mp="39ozq4i183kts">
                                {idx + 1}.
                              </span>
                              {rx.drugName} - {rx.dose}
                            </span>
                            <span className="text-gray-500 text-xs whitespace-nowrap" data-id="element-6389" data-mp="6784dldojx41">
                              {rx.frequency} x {rx.days} days
                            </span>
                            {rx.status === 'Pending' ? <button onClick={() => handleDeletePrescription(rx.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors flex-shrink-0" title="Remove this drug" aria-label={`Remove ${rx.drugName}`} data-id="element-6390" data-mp="3shkf3odsx5sb">
                                <Trash2 className="w-4 h-4" data-id="element-6391" data-mp="sevhdrrffoap" />
                              </button> : <span className="text-xs text-gray-400 flex-shrink-0 italic" data-id="element-6392" data-mp="11h4mu37u8rh8">
                                {rx.status}
                              </span>}
                          </li>)}
                      </ul>
                    </div>}
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 flex-shrink-0 sticky bottom-0 rounded-b-lg z-10" data-id="element-6393" data-mp="1g8dlf1tpxn6i">
                  <Button onClick={() => setShowRxModal(false)} className="bg-blue-600 hover:bg-blue-700 text-white" data-id="element-6394" data-mp="1p5w9yg6kmjn5">
                    Done
                  </Button>
                </div>
              </div>
            </div>
          </div>}

        {/* Image Zoom Modal */}
        {zoomImage && <div className="fixed inset-0 bg-black bg-opacity-90 flex flex-col items-center justify-center z-50" onClick={() => setZoomImage(null)} data-id="element-6395" data-mp="t50l49uav01y">
            <div className="absolute top-4 right-4 flex items-center gap-3 z-10" data-id="element-6396" data-mp="1725pm5ed7qv3">
              <span className="text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded" data-id="element-6397" data-mp="124483owl7zlq">
                Scroll to zoom • Click outside to close
              </span>
              <button className="text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2" onClick={() => setZoomImage(null)} data-id="element-6398" data-mp="1c2tp7fgntg0x">
                <X className="w-6 h-6" data-id="element-6399" data-mp="1luhaeymmi0ty" />
              </button>
            </div>
            <div className="max-w-[90vw] max-h-[85vh] overflow-auto rounded-lg" onClick={e => e.stopPropagation()} data-id="element-6400" data-mp="1zso0hslbywj2">
              <img src={zoomImage} alt="Radiology Scan — Zoom up to 200%" className="min-w-[60vw] max-w-none" style={{
            width: '180%',
            cursor: 'grab'
          }} data-id="element-6401" data-mp="1us004gb7p3c6" />
            </div>
            <p className="text-gray-400 text-xs mt-3" data-id="element-6402" data-mp="24jt3b5t726zn">
              Image displayed at up to 200% zoom. Scroll within the frame to
              pan.
            </p>
          </div>}

        {/* ─── TEST FILE MODAL (View File per investigation) ─── */}
        {viewingTest && selectedEncounter && (() => {
        const patient = getPatient(selectedEncounter.patientId);
        if (!patient) return null;
        const lab = viewingTest.kind === 'lab' ? selectedEncounter.labRequests.find(l => l.id === viewingTest.labId) : null;
        const rad = viewingTest.kind === 'rad' ? selectedEncounter.radiologyRequests.find(r => r.id === viewingTest.radId) : null;
        if (!lab && !rad) return null;
        const focusedTest = viewingTest.kind === 'lab' ? viewingTest.testName : null;
        // Filter lab.results by the requested test name when possible
        const labResultsFiltered = lab && lab.results ? focusedTest ? Object.fromEntries(Object.entries(lab.results).filter(([k]) => k.toLowerCase().includes(focusedTest.toLowerCase()) || focusedTest.toLowerCase().includes(k.toLowerCase()) ||
        // FBC sub-tests, urinalysis (UA_), testosterone — keep all if focused test is one of these umbrella tests
        ['fbc', 'urinalysis', 'testosterone', 'sperm analysis'].some(u => focusedTest.toLowerCase().includes(u)))) : lab.results : null;
        return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-6403" data-mp="3g6ut36uczayf">
                <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-6404" data-mp="2ijvvmtvudyzh">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-6405" data-mp="2senoxlie5zog">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-6406" data-mp="1zsl1m3utl63d">
                      {lab ? `Laboratory Result — ${focusedTest || lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).join(', ')}` : `Radiology Result — ${rad?.scanType}`}
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-6407" data-mp="1jftoqvynkbrz">
                      <Button variant="outline" size="sm" onClick={handlePrintTestModal} data-id="element-6408" data-mp="15hc4j87yhhet">
                        <Printer className="w-4 h-4 mr-1" data-id="element-6409" data-mp="vr0wb9h8om24" /> Print
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleDownloadTestPdf} disabled={isGeneratingTestPdf} data-id="element-6410" data-mp="10h7nnh34yko2">
                        <Download className="w-4 h-4 mr-1" data-id="element-6411" data-mp="qsfvp8y2s0d5" />
                        {isGeneratingTestPdf ? 'Generating...' : 'Download PDF'}
                      </Button>
                      <button onClick={closeTestModal} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close" data-id="element-6412" data-mp="hr9dmvfs2hhs">
                        <X className="w-5 h-5 text-gray-500" data-id="element-6413" data-mp="3pciwwwm9kgwc" />
                      </button>
                    </div>
                  </div>

                  <div ref={testModalRef} className="p-8 bg-white" data-id="element-6414" data-mp="73jiod3chz2b">
                    <div className="flex justify-between items-start mb-6 border-b-2 border-black pb-4" data-id="element-6415" data-mp="32purjpwkk9f">
                      <div className="flex items-center" data-id="element-6416" data-mp="h2hcnvjjiksp">
                        <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-6417" data-mp="c5h67jis3g12" />
                        <div data-id="element-6418" data-mp="lx4x9jbfav20">
                          <h1 className="text-xl font-bold text-pink-600 uppercase" data-id="element-6419" data-mp="zts4cex3b1ln">
                            BMED MEDICAL CENTRE
                          </h1>
                          <p className="text-xs italic text-gray-600" data-id="element-6420" data-mp="utm8emjsesxr">
                            Caring Healing Leading
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm" data-id="element-6421" data-mp="1iqy8ibpmx71">
                        <p className="font-bold uppercase border-b border-black pb-1 mb-2" data-id="element-6422" data-mp="1h63ospebe96">
                          {lab ? 'Laboratory Report' : 'Radiology Report'}
                        </p>
                        <p data-id="element-6423" data-mp="b80nlqf661rg">
                          <span className="font-semibold" data-id="element-6424" data-mp="bawlxfuxisgf">Patient:</span>{' '}
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p data-id="element-6425" data-mp="l7824gxn42g5">
                          <span className="font-semibold" data-id="element-6426" data-mp="l4vm49bg025q">File No:</span>{' '}
                          {patient.bmcFileNumber}
                        </p>
                        <p data-id="element-6427" data-mp="2mz4koai86ckf">
                          <span className="font-semibold" data-id="element-6428" data-mp="2mzwaf4p55g2s">Age/Sex:</span>{' '}
                          {patient.age} / {patient.sex}
                        </p>
                        <p data-id="element-6429" data-mp="2jh76d2bd3ray">
                          <span className="font-semibold" data-id="element-6430" data-mp="2jhgm7x2p0i2x">Date:</span>{' '}
                          {new Date((lab?.completedAt || rad?.completedAt) ?? Date.now()).toLocaleString()}
                        </p>
                        {lab?.performedBy && <p data-id="element-6431" data-mp="32ernfn7vxll0">
                            <span className="font-semibold" data-id="element-6432" data-mp="32dq8h7nr0ey0">Performed By:</span>{' '}
                            {lab.performedBy}
                          </p>}
                      </div>
                    </div>

                    {lab && <div data-id="element-6433" data-mp="2ofrw27szmsl5">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-6434" data-mp="2oegrw6vmriti">
                          {focusedTest || 'Tests Performed'}
                        </h2>

                        {/* ─── Structured FBC Form ─── */}
                        {focusedTest && isFBCTest(focusedTest) ? <div className="space-y-4" data-id="element-6435" data-mp="30vq6xgc0kwso">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-6436" data-mp="35nhox2h3tu77">
                              <h3 className="font-bold uppercase" data-id="element-6437" data-mp="35pceiu3bmahm">
                                Full Blood Count (FBC) Report
                              </h3>
                            </div>
                            {fbcStructure.map(cat => <div key={cat.category} className="border border-black" data-id="element-6438" data-mp="3jokevqb7zone">
                                <div className="bg-gray-100 p-2 font-bold border-b border-black text-xs uppercase" data-id="element-6439" data-mp="3andtb2tprnna">
                                  {cat.category}
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-6440" data-mp="1z6e44g2nxoxh">
                                  <thead data-id="element-6441" data-mp="2471v51qw65rp">
                                    <tr className="bg-gray-50" data-id="element-6442" data-mp="247tpk98r5xge">
                                      <th className="border border-black p-1.5 text-left" data-id="element-6443" data-mp="1voqdupxab0r2">
                                        Test
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-6444" data-mp="25nfl7ork12t2">
                                        Result
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-6445" data-mp="1cz09bb3pz0ly">
                                        Reference Range
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-24" data-id="element-6446" data-mp="17y2v19ltyqpq">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-6447" data-mp="u6y1mp922unu">
                                    {cat.tests.map(t => <tr key={t.name} data-id="element-6448" data-mp="p95wuunbbe28">
                                        <td className="border border-black p-1.5" data-id="element-6449" data-mp="2pc721rzxncax">
                                          {t.name}
                                        </td>
                                        <td className="border border-black p-1.5 font-bold" data-id="element-6450" data-mp="2z1h0dplddr7h">
                                          {lab.results && lab.results[t.name] || '—'}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-6451" data-mp="2bbe93bpa4p9x">
                                          {t.range}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-6452" data-mp="2kccf1zja36v5">
                                          {t.unit}
                                        </td>
                                      </tr>)}
                                  </tbody>
                                </table>
                              </div>)}
                          </div> : focusedTest && isUrinalysisTest(focusedTest) ? <div data-id="element-6453" data-mp="3w5388jocj6sw">
                            <div className="text-center border-b-2 border-black pb-2 mb-6" data-id="element-6454" data-mp="3w4bme0kjwnft">
                              <h3 className="font-bold uppercase" data-id="element-6455" data-mp="3s3rsd5v9xzxw">
                                Urinalysis Report
                              </h3>
                            </div>
                            <div className="max-w-md mx-auto space-y-2" data-id="element-6456" data-mp="3i52cfimxupka">
                              {urinalysisTests.map(t => {
                      const key = `UA_${t.name}`;
                      const value = lab.results && (lab.results[key] || lab.results[t.name]) || '';
                      return <div key={t.name} className="flex items-center text-sm" data-id="element-6457" data-mp="1qnv835cdxto6">
                                    <span className="w-16 font-bold" data-id="element-6458" data-mp="1gvxvbu5qatnc">
                                      {t.name}
                                    </span>
                                    <span className="mx-3 text-gray-400" data-id="element-6459" data-mp="16yt6tnoeum3d">
                                      -
                                    </span>
                                    <span className="flex-1 border-b border-gray-400 pb-0.5 font-medium" data-id="element-6460" data-mp="1bypepy08axo7">
                                      {value || '\u00A0'}
                                    </span>
                                  </div>;
                    })}
                            </div>
                            <div className="max-w-md mx-auto mt-10 grid grid-cols-2 gap-4 text-sm" data-id="element-6461" data-mp="2xb8uqsqzl4vw">
                              <div data-id="element-6462" data-mp="37f76k3bkb3h8">
                                <p className="font-semibold mb-2" data-id="element-6463" data-mp="37g8u379rlifu">
                                  Dr.'s Name:
                                </p>
                                <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-6464" data-mp="32e9nloiqa5eo">
                                  {lab.results && lab.results['UA_DR_NAME'] || '\u00A0'}
                                </p>
                              </div>
                              <div data-id="element-6465" data-mp="3q7rtl44kyed4">
                                <p className="font-semibold mb-2" data-id="element-6466" data-mp="3l73tlsydin6r">Signature:</p>
                                <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-6467" data-mp="30z7ln4pmugp">
                                  {lab.results && lab.results['UA_DR_SIG'] || '\u00A0'}
                                </p>
                              </div>
                            </div>
                          </div> : focusedTest && isTestosteroneTest(focusedTest) ? <div className="space-y-2" data-id="element-6468" data-mp="fz06z5xot3d4">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-6469" data-mp="g1mljpizwj4u">
                              <h3 className="font-bold uppercase" data-id="element-6470" data-mp="b2t4xo8ewc0g">
                                Testosterone Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-6471" data-mp="kxl61217dgzz">
                              <thead data-id="element-6472" data-mp="yxcopg2nzlkb">
                                <tr className="bg-gray-100" data-id="element-6473" data-mp="ywb3iirxpiov">
                                  <th className="border border-black p-1.5 text-left" data-id="element-6474" data-mp="tz1gtavxjl4q">
                                    Test
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-6475" data-mp="13ouink69slvs">
                                    Result
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-6476" data-mp="1cq2elvlbdamp">
                                    Reference (Male)
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-6477" data-mp="1w9rh1zh4cafz">
                                    Reference (Female)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-6478" data-mp="1namiit5k4h3t">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-6479" data-mp="1ibt510q9sq3n">
                                {testosteroneTests.map(t => <tr key={t.name} data-id="element-6480" data-mp="13ngisvgzilmc">
                                    <td className="border border-black p-1.5" data-id="element-6481" data-mp="13ozz2m2n4wo4">
                                      {t.name}
                                    </td>
                                    <td className="border border-black p-1.5 font-bold" data-id="element-6482" data-mp="um8n8l5uryvy">
                                      {lab.results && (lab.results[`TESTO_${t.name}`] || lab.results[t.name]) || '—'}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-6483" data-mp="2rkoaprsdslyc">
                                      {t.rangeMale}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-6484" data-mp="2nhj8lqc7qma8">
                                      {t.rangeFemale}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-6485" data-mp="3fbbj0e67ldlf">
                                      {t.unit}
                                    </td>
                                  </tr>)}
                              </tbody>
                            </table>
                          </div> : focusedTest && isHormonalProfileTest(focusedTest) ? <div data-id="element-6486" data-mp="adfm9n1iffl8">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-6487" data-mp="ae7f4q3q2z7t">
                              <h3 className="font-bold uppercase" data-id="element-6488" data-mp="fc8yzd8z32kc">
                                Hormonal Profile Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-6489" data-mp="o4ufsnt1mz6j">
                              <thead data-id="element-6490" data-mp="t3xabebedn09">
                                <tr className="bg-gray-100" data-id="element-6491" data-mp="t2w1lghutclp">
                                  <th className="border border-black p-1.5 text-left" data-id="element-6492" data-mp="y1fk16x8o69b">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-6493" data-mp="172nbp65928uf">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-6494" data-mp="34v18y2u3q5vc">
                                    Reference Range
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-6495" data-mp="2diaafddlp5bq">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-6496" data-mp="2ijg71madtcqj">
                                {hormonalProfileTests.map(h => <Fragment key={h.key} data-id="element-6497" data-mp="1ut2m0fq7se5g">
                                    <tr className="align-top" data-id="element-6498" data-mp="1urj6wsgao959">
                                      <td className="border border-black p-1.5 font-medium" data-id="element-6499" data-mp="1pvu147dolw0i">
                                        {h.name}
                                      </td>
                                      <td className="border border-black p-1.5 font-bold" data-id="element-6500" data-mp="1zsf4koa1yz28">
                                        {lab.results && (lab.results[`HP_${h.key}`] || lab.results[h.name] || lab.results[h.key]) || '—'}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-6501" data-mp="3nk6aqb651vb1">
                                        {h.ranges.map(r => <div key={r.label} data-id="element-6502" data-mp="3dtbm505f6jmf">
                                            <span className="font-medium" data-id="element-6503" data-mp="5cgru92ou7jy">
                                              {r.label}:
                                            </span>{' '}
                                            {r.value}
                                          </div>)}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-6504" data-mp="19238u0ooiuw">
                                        {h.unit}
                                      </td>
                                    </tr>
                                    <tr data-id="element-6505" data-mp="rkw01p64dugd">
                                      <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-6506" data-mp="rjuakfocx5f4">
                                        Method: {h.method}
                                      </td>
                                    </tr>
                                  </Fragment>)}
                              </tbody>
                            </table>
                          </div> : focusedTest && isAMHTest(focusedTest) ? <div data-id="element-6507" data-mp="3v3p0r1dsuot4">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-6508" data-mp="3r0iuhtqosxmn">
                              <h3 className="font-bold uppercase" data-id="element-6509" data-mp="1t9zf22ht8cll">
                                Anti Mullerian Hormone (AMH) Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-6510" data-mp="376sohq1xc7xd">
                              <thead data-id="element-6511" data-mp="3c5dq9twtqznx">
                                <tr className="bg-gray-100" data-id="element-6512" data-mp="3c4c1ynqssgo9">
                                  <th className="border border-black p-1.5 text-left" data-id="element-6513" data-mp="3gw2f11h4dijd">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-6514" data-mp="3kzs0hi2hzk06">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-6515" data-mp="2t7axr552ikld">
                                    Reference Range (by age, ng/mL)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-6516" data-mp="327z7hnnfhysg">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-6517" data-mp="3p9sopp6itcxv">
                                <tr className="align-top" data-id="element-6518" data-mp="3paud0vcjrvxj">
                                  <td className="border border-black p-1.5 font-medium" data-id="element-6519" data-mp="3kaoh32z8vn9n">
                                    {amhStructure.name}
                                  </td>
                                  <td className="border border-black p-1.5 font-bold" data-id="element-6520" data-mp="1ytdvkmghr3b">
                                    {lab.results && (lab.results[amhStructure.storageKey] || lab.results[focusedTest] || lab.results['AMH'] || lab.results['Anti-Müllerian Hormone'] || lab.results['Anti Mullerian Hormone (AMH)']) || '—'}
                                  </td>
                                  <td className="border border-black p-1.5 text-xs" data-id="element-6521" data-mp="2in5kjgwlgxlx">
                                    {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-6522" data-mp="2cj92zuffcq5u">
                                        <span className="font-medium" data-id="element-6523" data-mp="2cktvlei6hz12">
                                          {a.ageBand}:
                                        </span>{' '}
                                        {a.range}
                                      </div>)}
                                  </td>
                                  <td className="border border-black p-1.5 text-xs" data-id="element-6524" data-mp="1ttkkj0srf27d">
                                    {amhStructure.unit}
                                  </td>
                                </tr>
                                <tr data-id="element-6525" data-mp="1ksd09mn2w1wa">
                                  <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-6526" data-mp="3a6rl3vuz74mi">
                                    Method: {amhStructure.method}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                            <div className="mt-3 border border-amber-300 bg-amber-50 rounded p-3 text-xs" data-id="element-6527" data-mp="upigrbqv9v43">
                              <p className="font-bold uppercase mb-1" data-id="element-6528" data-mp="zhsc57ar3ndr">
                                Interpretation
                              </p>
                              <ul className="list-disc pl-5 space-y-0.5 text-gray-700" data-id="element-6529" data-mp="18hf4l3li5iuo">
                                {amhStructure.interpretation.map((line, i) => <li key={i} data-id="element-6530" data-mp="1dep1t5f4uyt2" data-mpi={i}>{line}</li>)}
                              </ul>
                            </div>
                          </div> : focusedTest && isHCGTest(focusedTest) ? <div data-id="element-6531" data-mp="3k89b9yzlpkhf">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-6532" data-mp="3k7hpffvt314c">
                              <h3 className="font-bold uppercase" data-id="element-6533" data-mp="21del3x5ja1xc">
                                Beta HCG (Quantitative) Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-6534" data-mp="1sk1l6b91y01r">
                              <thead data-id="element-6535" data-mp="1nm01pp7prypr">
                                <tr className="bg-gray-100" data-id="element-6536" data-mp="1nivpq5o0byoj">
                                  <th className="border border-black p-1.5 text-left" data-id="element-6537" data-mp="2k9oahglvyewn">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-6538" data-mp="2b9hxrwy0hiyj">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-6539" data-mp="zv4bgwiwpbkm">
                                    Reference Range (mIU/mL)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-6540" data-mp="uydywj6b8y3y">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-6541" data-mp="nbgwdrw9uf0p">
                                {(() => {
                        const hcgValue = lab.results && (lab.results[hcgStructure.storageKey] || lab.results[focusedTest] || lab.results['HCG'] || lab.results['Beta HCG']) || '';
                        const abnormal = isHCGAbnormal(hcgValue);
                        return <>
                                      <tr className="align-top" data-id="element-6542" data-mp="3vsbxb1tidpc5">
                                        <td className="border border-black p-1.5 font-medium" data-id="element-6543" data-mp="3vrk2ht7qjvmx">
                                          {hcgStructure.name}
                                        </td>
                                        <td className={`border border-black p-1.5 font-bold ${abnormal ? 'text-red-600 bg-red-50' : ''}`} data-id="element-6544" data-mp="379a1ducbuk7b">
                                          {hcgValue || '—'}
                                          {abnormal && <div className="text-xs font-normal mt-0.5" data-id="element-6545" data-mp="3q6uoowzg1apt">
                                              Above normal threshold (&gt; 6
                                              mIU/mL)
                                            </div>}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-6546" data-mp="hqrjcxvszy4n">
                                          {hcgStructure.ranges.map(r => <div key={r.label} data-id="element-6547" data-mp="276u1g4gd2ak1">
                                              <span className="font-medium" data-id="element-6548" data-mp="2c6gfgg40jutw">
                                                {r.label}:
                                              </span>{' '}
                                              {r.range}
                                            </div>)}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-6549" data-mp="1t7ued18hy3pz">
                                          {hcgStructure.unit}
                                        </td>
                                      </tr>
                                      <tr data-id="element-6550" data-mp="229lfdss3dmdi">
                                        <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-6551" data-mp="2267dsmr95h74">
                                          Method: {hcgStructure.method}
                                        </td>
                                      </tr>
                                    </>;
                      })()}
                              </tbody>
                            </table>
                          </div> : labResultsFiltered && Object.keys(labResultsFiltered).length > 0 ? <table className="w-full border-collapse text-sm" data-id="element-6552" data-mp="e9q20yx260nq">
                            <thead data-id="element-6553" data-mp="a8o0ucjkzej4">
                              <tr className="bg-gray-100" data-id="element-6554" data-mp="a9ppxkxfm1ju">
                                <th className="border border-black p-2 text-left" data-id="element-6555" data-mp="5amixuxw5l98">
                                  Parameter
                                </th>
                                <th className="border border-black p-2 text-left" data-id="element-6556" data-mp="24l4ehyephuy0">
                                  Result
                                </th>
                              </tr>
                            </thead>
                            <tbody data-id="element-6557" data-mp="2nj7f7muuuuw8">
                              {Object.entries(labResultsFiltered).map(([k, v]) => <tr key={k} data-id="element-6558" data-mp="29tvrofkvdf1a">
                                    <td className="border border-black p-2 font-medium" data-id="element-6559" data-mp="2eqm8j54i0enr">
                                      {k}
                                    </td>
                                    <td className="border border-black p-2" data-id="element-6560" data-mp="32evkzgftnq28">
                                      {v}
                                    </td>
                                  </tr>)}
                            </tbody>
                          </table> : <p className="text-gray-500 italic" data-id="element-6561" data-mp="1h1skoybxyidc">
                            No structured results recorded for this test.
                          </p>}

                        {lab.labComments && <div className="mt-6 border border-gray-300 bg-gray-50 rounded p-4" data-id="element-6562" data-mp="19jurk4e2hjwa">
                            <h3 className="font-bold uppercase mb-2 text-sm" data-id="element-6563" data-mp="19m78qfc03q8a">
                              Comments / Observations / Interpretation
                            </h3>
                            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800" data-id="element-6564" data-mp="1iig4nk0rs6po">
                              {lab.labComments}
                            </pre>
                          </div>}
                      </div>}

                    {rad && <div data-id="element-6565" data-mp="zu1fl2a0j2ri">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-6566" data-mp="ztt1x862ftf9">
                          {rad.scanType}
                        </h2>
                        {rad.radiographerName && <p className="text-sm mb-3" data-id="element-6567" data-mp="75x4u2cl3zhy">
                            <span className="font-semibold" data-id="element-6568" data-mp="73b00ah2vy3f">
                              Sonographer / Radiographer:
                            </span>{' '}
                            {rad.radiographerName}
                          </p>}
                        {rad.report && <div className="mb-4 border border-black p-4" data-id="element-6569" data-mp="3rrz9k0txhbcq">
                            <pre className="whitespace-pre-wrap font-sans text-sm" data-id="element-6570" data-mp="kweuzevsmxr">
                              {rad.report}
                            </pre>
                          </div>}
                        {rad.images && rad.images.length > 0 && <div data-id="element-6571" data-mp="ec189jrnoizn">
                            <div className="flex justify-between items-center mb-3 print:hidden" data-id="element-6572" data-mp="ect3gthccepe">
                              <h3 className="font-bold" data-id="element-6573" data-mp="jbxhpyj4h2ml">Scan Images</h3>
                              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1.5 rounded-lg" data-id="element-6574" data-mp="oah2hvlzc8dd">
                                <button onClick={() => setTestImageZoom(z => Math.max(10, z - 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom out" data-id="element-6575" data-mp="t88ragrbvedc">
                                  <ZoomOut className="w-4 h-4" data-id="element-6576" data-mp="2wh2d60ra6lqo" />
                                </button>
                                <input type="range" min={10} max={200} step={10} value={testImageZoom} onChange={e => setTestImageZoom(Number(e.target.value))} className="w-32" data-id="element-6577" data-mp="2rh69y3qeqyc6" />
                                <button onClick={() => setTestImageZoom(z => Math.min(200, z + 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom in" data-id="element-6578" data-mp="14wjjvhgwqusg">
                                  <ZoomIn className="w-4 h-4" data-id="element-6579" data-mp="1jkmrevalmejd" />
                                </button>
                                <span className="text-sm font-medium w-12 text-right" data-id="element-6580" data-mp="1etoabthsdcf2">
                                  {testImageZoom}%
                                </span>
                              </div>
                            </div>
                            <div className="space-y-4 print:space-y-2" data-id="element-6581" data-mp="27b0mkuohcti6">
                              {rad.images.map((img, idx) => <div key={idx} className="border-2 border-gray-300 rounded overflow-auto bg-gray-50 max-h-[600px] print:max-h-none print:overflow-visible" data-id="element-6582" data-mp="22hnxykjswmpf" data-mpi={idx}>
                                  <div style={{
                        transform: `scale(${testImageZoom / 100})`,
                        transformOrigin: 'top left',
                        display: 'inline-block'
                      }} data-id="element-6583" data-mp="2jr9umdriasob">
                                    <img src={img} alt={`${rad.scanType} ${idx + 1}`} className="block" data-id="element-6584" data-mp="32kdn3vszmzhc" />
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>}
                  </div>
                </div>
              </div>;
      })()}
      </div>;
  }
  return <div className="space-y-6" data-id="element-6585" data-mp="1q88fydbf2soc">
      <div className="flex justify-between items-center" data-id="element-6586" data-mp="1q8i9ubzl1jzb">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-6587" data-mp="1lf5l81uwld6k">
          Clinician Dashboard
        </h1>
        <div className="flex space-x-2" data-id="element-6588" data-mp="203igsxq2wu0j">
          <Badge variant="info" className="text-sm px-3 py-1" data-id="element-6589" data-mp="1v5qs0ckqdo0k">
            {pendingEncounters.length} Patients Waiting
          </Badge>
          <Badge variant="success" className="text-sm px-3 py-1 bg-green-100 text-green-800" data-id="element-6590" data-mp="295rpdi1sedba">
            {completedEncounters.length} Completed
          </Badge>
        </div>
      </div>

      <nav aria-label="Clinician services" data-id="element-6591" data-mp="es0lkz652nnr">
        <ClinicianServiceCards activeTab={activeTab} onSelect={setActiveTab} data-id="element-6592" data-mp="9sfdnz8zr4yw" />
      </nav>

      {activeTab === 'amend' && <AmendFile data-id="element-6593" data-mp="2lh28oqjznqn7" />}

      {activeTab === 'queue' && <Card noPadding data-id="element-6594" data-mp="2qekmovcv4w22">
          <ul className="divide-y divide-gray-200" data-id="element-6595" data-mp="2qf3w1nog6c4h">
            {pendingEncounters.length > 0 ? pendingEncounters.map(encounter => {
          const patient = getPatient(encounter.patientId);
          if (!patient) return null;
          const isInvestigations = encounter.stage === 'INVESTIGATIONS';
          const hasResults = isInvestigations && !encounter.pendingLab && !encounter.pendingRadiology;
          const hasDraft = !!encounter.clinicalNote;
          const isPharmacy = encounter.stage === 'PHARMACY';
          const iconBg = hasResults ? 'bg-green-100' : isPharmacy ? 'bg-pink-100' : isInvestigations ? 'bg-yellow-100' : 'bg-blue-100';
          const iconColor = hasResults ? 'text-green-600' : isPharmacy ? 'text-pink-600' : isInvestigations ? 'text-yellow-600' : 'text-blue-600';
          return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-6596" data-mp="2ogspgwnic05o">
                    <div className="flex items-center" data-id="element-6597" data-mp="2kgim7rr3nnui">
                      <div className={`p-2 rounded-full mr-4 ${iconBg}`} data-id="element-6598" data-mp="2fn7bhfj62mxb">
                        <Stethoscope className={`h-6 w-6 ${iconColor}`} data-id="element-6599" data-mp="2ao31wnsbyn6g" />
                      </div>
                      <div data-id="element-6600" data-mp="25qkr0rur5xvt">
                        <p className="text-sm font-medium text-blue-600" data-id="element-6601" data-mp="25rcm81kfttlk">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-6602" data-mp="1wpd5zbh8ytjo">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1" data-id="element-6603" data-mp="c95ahw9fmkjj">
                          <ConsultationTypeBadge type={encounter.consultationType} size="sm" data-id="element-6604" data-mp="g7crms7t5nv9" />
                          <Badge variant="default" data-id="element-6605" data-mp="q7daye5og3zg">{patient.age} yrs</Badge>
                          {hasResults && <Badge variant="success" data-id="element-6606" data-mp="3opsej3dan8sy">Results Ready</Badge>}
                          {isInvestigations && encounter.pendingLab && <Badge variant="warning" data-id="element-6607" data-mp="2gr954m9nfxw">Awaiting Lab</Badge>}
                          {isInvestigations && encounter.pendingRadiology && <Badge variant="warning" data-id="element-6608" data-mp="35y9d30yx42t1">Awaiting Radiology</Badge>}
                          {isPharmacy && <Badge variant="purple" data-id="element-6609" data-mp="3avhwf4vsycvv">At Pharmacy</Badge>}
                          {hasDraft && encounter.stage === 'CLINICIAN' && <Badge variant="info" data-id="element-6610" data-mp="2yvkvzn9a2qzg">Draft Saved</Badge>}
                        </div>
                      </div>
                    </div>
                    <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-6611" data-mp="37nd7m1a1g65m">
                      Open File
                    </Button>
                  </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-6612" data-mp="2fwfhn4z22znu">
                <Stethoscope className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-6613" data-mp="2fya08cmuufor" />
                <p className="text-lg font-medium text-gray-900" data-id="element-6614" data-mp="2twzxptdl6hdn">
                  No patients waiting
                </p>
              </li>}
          </ul>
        </Card>}

      {activeTab === 'completed' && <Card data-id="element-6615" data-mp="46d2n9bfobm3">
          <div className="mb-4" data-id="element-6616" data-mp="46w9nuzjpflc">
            <input type="text" placeholder="Search completed consultations by patient name or file number..." className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-6617" data-mp="45l7fzlr0fwc" />
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-6618" data-mp="1i3fsbsqsizqd">
            <ul className="divide-y divide-gray-200" data-id="element-6619" data-mp="25rqlsau5px4m">
              {filteredCompleted.length > 0 ? filteredCompleted.map(encounter => {
            const patient = getPatient(encounter.patientId);
            if (!patient) return null;
            return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-6620" data-mp="210gznk7thbxh">
                      <div className="flex items-center" data-id="element-6621" data-mp="2thb6oz9vf4gj">
                        <div className="p-2 rounded-full mr-4 bg-green-100" data-id="element-6622" data-mp="2tiw1mq03kpex">
                          <CheckCircle className="h-6 w-6 text-green-600" data-id="element-6623" data-mp="tg3jueh87evo" />
                        </div>
                        <div data-id="element-6624" data-mp="onto2htfjklh">
                          <p className="text-sm font-medium text-blue-600" data-id="element-6625" data-mp="omilglbq0ivy">
                            {patient.bmcFileNumber}
                          </p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-6626" data-mp="ergt7gm5f0p5">
                            {patient.firstName} {patient.lastName}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-1 text-sm text-gray-500" data-id="element-6627" data-mp="17fcqamfmqumg">
                            <span data-id="element-6628" data-mp="12fq9y44i8y9f">
                              {new Date(encounter.startedAt).toLocaleDateString()}
                            </span>
                            <span data-id="element-6629" data-mp="3n6hbjivl6uzo">•</span>
                            <span data-id="element-6630" data-mp="3n7k3zv8k55hp">
                              Attended by:{' '}
                              {encounter.lastAttendingClinician || 'Unknown'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-6631" data-mp="22ozpdzj90g2x">
                        Open File
                      </Button>
                    </li>;
          }) : <li className="p-12 text-center text-gray-500" data-id="element-6632" data-mp="2cg2udcfatf7h">
                  <p className="text-lg font-medium text-gray-900" data-id="element-6633" data-mp="2hcb2i5v2w855">
                    No completed consultations found
                  </p>
                </li>}
            </ul>
          </div>
        </Card>}
    </div>;
};